import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import TagManagement from '../components/TagManagement';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { AppDrawer } from '../components/ui/AppDrawer';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Building2, Clock, Loader2, Check, Globe, Zap, Palette, Image, Phone, Mail, MapPin, Tag, Plus, Pencil, Trash2, Bell, Send, Sparkles, RotateCcw, Wrench, Info, CreditCard, FileText, Eye, EyeOff, CheckCircle2, AlertTriangle, Shield, MessageSquare, Upload, CalendarDays, ChevronRight, ChevronLeft, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ServiceTitanSettings } from '../components/integrations/ServiceTitanSettings';
import { IntegrationHealthPanel } from '../components/integrations/IntegrationHealthPanel';
import { StripeConnectSettings } from '../components/integrations/StripeConnectSettings';
import { HousecallProSettings } from '../components/integrations/HousecallProSettings';
import { housecallProAPI } from '../lib/housecallproApi';
import { CommunicationsSettings } from '../components/CommunicationsSettings';
import { COUNTRIES, INDUSTRIES, CURRENCIES, COUNTRY_CURRENCY_MAP, getCountryName, getIndustryLabel } from '../lib/i18n-config';
import { warehouseAPI, purchaseOrdersAPI, poNotificationSettingsAPI, notificationSettingsAPI, dailyDigestAPI, estimatesAPI, campaignsAPI, i18nSettingsAPI, settingsAPI } from '../lib/api';
import api from '../lib/api';
import { applyThemeToDOM, clearThemeFromDOM } from '../components/ThemeApplier';
import { 
  getAvailableLogos, 
  getActiveLogo, 
  getForceLogoOverride, 
  setForceLogoOverride,
  BRANDING_CONFIG
} from '../lib/brandingConfig';

// Common IANA timezones grouped by region
const TIMEZONE_OPTIONS = [
  { group: 'United States', timezones: [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST, no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  ]},
  { group: 'Canada', timezones: [
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
    { value: 'America/Winnipeg', label: 'Central Time (Winnipeg)' },
    { value: 'America/Edmonton', label: 'Mountain Time (Edmonton)' },
    { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
  ]},
  { group: 'Europe', timezones: [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  ]},
  { group: 'Australia', timezones: [
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST, no DST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
  ]},
  { group: 'Asia', timezones: [
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  ]},
];

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_THEME = {
  buttonColor: '',
  buttonTextColor: '',
  sidebarTop: '',
  sidebarMid: '',
  sidebarBottom: '',
  sidebarText: '',
};

const THEME_PRESETS = [
  {
    name: 'Default Blue',
    slug: 'default-blue',
    colors: { buttonColor: '', buttonTextColor: '', sidebarTop: '', sidebarMid: '', sidebarBottom: '', sidebarText: '' },
    preview: { sidebar: ['#2f6fe4', '#1e5bd6', '#1346b8'], button: '#1e5bd6' },
  },
  {
    name: 'Ocean Teal',
    slug: 'ocean-teal',
    colors: { buttonColor: '#0d9488', buttonTextColor: '#ffffff', sidebarTop: '#0f766e', sidebarMid: '#0d6963', sidebarBottom: '#064e3b', sidebarText: '#e0faf5' },
    preview: { sidebar: ['#0f766e', '#0d6963', '#064e3b'], button: '#0d9488' },
  },
  {
    name: 'Forest Green',
    slug: 'forest-green',
    colors: { buttonColor: '#16a34a', buttonTextColor: '#ffffff', sidebarTop: '#15803d', sidebarMid: '#166534', sidebarBottom: '#052e16', sidebarText: '#dcfce7' },
    preview: { sidebar: ['#15803d', '#166534', '#052e16'], button: '#16a34a' },
  },
  {
    name: 'Sunset Orange',
    slug: 'sunset-orange',
    colors: { buttonColor: '#ea580c', buttonTextColor: '#ffffff', sidebarTop: '#c2410c', sidebarMid: '#9a3412', sidebarBottom: '#431407', sidebarText: '#fff7ed' },
    preview: { sidebar: ['#c2410c', '#9a3412', '#431407'], button: '#ea580c' },
  },
  {
    name: 'Midnight Purple',
    slug: 'midnight-purple',
    colors: { buttonColor: '#7c3aed', buttonTextColor: '#ffffff', sidebarTop: '#6d28d9', sidebarMid: '#5b21b6', sidebarBottom: '#2e1065', sidebarText: '#ede9fe' },
    preview: { sidebar: ['#6d28d9', '#5b21b6', '#2e1065'], button: '#7c3aed' },
  },
  {
    name: 'Slate Pro',
    slug: 'slate-pro',
    colors: { buttonColor: '#475569', buttonTextColor: '#ffffff', sidebarTop: '#334155', sidebarMid: '#1e293b', sidebarBottom: '#0f172a', sidebarText: '#e2e8f0' },
    preview: { sidebar: ['#334155', '#1e293b', '#0f172a'], button: '#475569' },
  },
  {
    name: 'Ruby Red',
    slug: 'ruby-red',
    colors: { buttonColor: '#dc2626', buttonTextColor: '#ffffff', sidebarTop: '#b91c1c', sidebarMid: '#991b1b', sidebarBottom: '#450a0a', sidebarText: '#fef2f2' },
    preview: { sidebar: ['#b91c1c', '#991b1b', '#450a0a'], button: '#dc2626' },
  },
  {
    name: 'Warm Amber',
    slug: 'warm-amber',
    colors: { buttonColor: '#d97706', buttonTextColor: '#ffffff', sidebarTop: '#b45309', sidebarMid: '#92400e', sidebarBottom: '#451a03', sidebarText: '#fffbeb' },
    preview: { sidebar: ['#b45309', '#92400e', '#451a03'], button: '#d97706' },
  },
];

function ColorInput({ label, value, onChange, defaultHint, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded border border-input cursor-pointer p-0.5"
          data-testid={testId}
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={defaultHint || '#hex'}
          className="font-mono text-sm flex-1"
          data-testid={`${testId}-text`}
        />
      </div>
    </div>
  );
}

function ThemeCustomizationCard() {
  const [theme, setTheme] = useState({ ...DEFAULT_THEME });
  const [original, setOriginal] = useState({ ...DEFAULT_THEME });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await warehouseAPI.getCompanyBranding();
        const t = res.data?.theme || {};
        const loaded = {
          buttonColor: t.buttonColor || '',
          buttonTextColor: t.buttonTextColor || '',
          sidebarTop: t.sidebarTop || '',
          sidebarMid: t.sidebarMid || '',
          sidebarBottom: t.sidebarBottom || '',
          sidebarText: t.sidebarText || '',
        };
        setTheme(loaded);
        setOriginal(loaded);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const hasChanges = JSON.stringify(theme) !== JSON.stringify(original);

  const handleChange = (key, value) => {
    setTheme(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(theme).forEach(([k, v]) => { payload[k] = v || null; });
      await warehouseAPI.updateCompanyBranding({ theme: payload });
      setOriginal({ ...theme });
      applyThemeToDOM(payload);
      window.dispatchEvent(new CustomEvent('theme-updated', { detail: payload }));
      toast.success('Theme saved', { description: 'Your custom theme has been applied.' });
    } catch (err) {
      toast.error('Failed to save theme', { description: err.response?.data?.detail || 'Please try again' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (group) => {
    if (group === 'buttons') {
      setTheme(prev => ({ ...prev, buttonColor: '', buttonTextColor: '' }));
    } else if (group === 'sidebar') {
      setTheme(prev => ({ ...prev, sidebarTop: '', sidebarMid: '', sidebarBottom: '', sidebarText: '' }));
    }
  };

  const handleResetAll = async () => {
    const cleared = { ...DEFAULT_THEME };
    setTheme(cleared);
    setSaving(true);
    try {
      const payload = { buttonColor: null, buttonTextColor: null, sidebarTop: null, sidebarMid: null, sidebarBottom: null, sidebarText: null };
      await warehouseAPI.updateCompanyBranding({ theme: payload });
      setOriginal(cleared);
      clearThemeFromDOM();
      window.dispatchEvent(new CustomEvent('theme-updated', { detail: null }));
      toast.success('Theme reset to defaults');
    } catch {
      toast.error('Failed to reset theme');
    } finally {
      setSaving(false);
    }
  };

  // Live preview: apply theme changes in real-time as user picks colors
  useEffect(() => {
    if (loading) return;
    const preview = {};
    Object.entries(theme).forEach(([k, v]) => { preview[k] = v || null; });
    applyThemeToDOM(preview);
  }, [theme, loading]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="theme-customization-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-primary" />
          <CardTitle>Theme Customization</CardTitle>
        </div>
        <CardDescription>
          Customize accent colors for buttons and the sidebar. Changes are previewed live.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Theme Presets */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Quick Presets</h4>
          <div className="grid grid-cols-4 sm:grid-cols-4 gap-2">
            {THEME_PRESETS.map((preset) => {
              const isActive = preset.colors.sidebarTop === theme.sidebarTop 
                && preset.colors.buttonColor === theme.buttonColor
                && preset.colors.sidebarMid === theme.sidebarMid;
              return (
                <button
                  key={preset.slug}
                  type="button"
                  onClick={() => setTheme({ ...preset.colors })}
                  className={`group relative rounded-lg border-2 p-2 transition-all hover:shadow-md ${
                    isActive ? 'border-primary ring-1 ring-primary/30' : 'border-border hover:border-primary/40'
                  }`}
                  data-testid={`theme-preset-${preset.slug}`}
                >
                  <div className="flex flex-col items-center gap-1.5">
                    {/* Sidebar gradient preview */}
                    <div
                      className="w-full h-8 rounded"
                      style={{
                        background: `linear-gradient(180deg, ${preset.preview.sidebar[0]} 0%, ${preset.preview.sidebar[1]} 50%, ${preset.preview.sidebar[2]} 100%)`,
                      }}
                    />
                    {/* Button color dot */}
                    <div className="flex items-center gap-1">
                      <div
                        className="w-3 h-3 rounded-full border border-black/10"
                        style={{ backgroundColor: preset.preview.button }}
                      />
                      <span className="text-[10px] font-medium text-muted-foreground truncate max-w-[70px]">
                        {preset.name}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t" />

        {/* Button Colors */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Button Colors</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleReset('buttons')}
              className="text-xs h-7"
              data-testid="reset-button-colors"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Reset
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <ColorInput
              label="Primary Button Background"
              value={theme.buttonColor}
              onChange={(v) => handleChange('buttonColor', v)}
              defaultHint="Default: blue"
              testId="theme-button-color"
            />
            <ColorInput
              label="Primary Button Text"
              value={theme.buttonTextColor}
              onChange={(v) => handleChange('buttonTextColor', v)}
              defaultHint="Default: white"
              testId="theme-button-text-color"
            />
          </div>
        </div>

        <div className="border-t" />

        {/* Sidebar Colors */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Sidebar Colors</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleReset('sidebar')}
              className="text-xs h-7"
              data-testid="reset-sidebar-colors"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Reset
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <ColorInput
              label="Sidebar Top"
              value={theme.sidebarTop}
              onChange={(v) => handleChange('sidebarTop', v)}
              defaultHint="#2f6fe4"
              testId="theme-sidebar-top"
            />
            <ColorInput
              label="Sidebar Middle"
              value={theme.sidebarMid}
              onChange={(v) => handleChange('sidebarMid', v)}
              defaultHint="#1e5bd6"
              testId="theme-sidebar-mid"
            />
            <ColorInput
              label="Sidebar Bottom"
              value={theme.sidebarBottom}
              onChange={(v) => handleChange('sidebarBottom', v)}
              defaultHint="#1346b8"
              testId="theme-sidebar-bottom"
            />
            <ColorInput
              label="Sidebar Nav Text"
              value={theme.sidebarText}
              onChange={(v) => handleChange('sidebarText', v)}
              defaultHint="Default: white"
              testId="theme-sidebar-text"
            />
          </div>
        </div>

        <div className="border-t" />

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetAll}
            disabled={saving}
            data-testid="reset-all-theme"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset All to Defaults
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="gap-1.5 text-xs sm:text-sm shrink-0"
            data-testid="save-theme-button"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : hasChanges ? (
              <Check className="w-4 h-4" />
            ) : null}
            {saving ? 'Saving...' : hasChanges ? 'Save Theme' : 'No Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function GuidedTourSettings() {
  const [tourState, setTourState] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const fetchState = React.useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API}/api/onboarding/tour-state?tour_id=activation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setTourState(await res.json());
    } catch {}
  }, []);

  React.useEffect(() => { fetchState(); }, [fetchState]);

  const updateState = async (action) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      await fetch(`${API}/api/onboarding/tour-state`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tour_id: "activation", action, tour_version: 1 }),
      });
      await fetchState();
      if (action === "restart") toast.success("Guided tour restarted — it will show on your next page load.");
    } catch {
      toast.error("Failed to update tour settings");
    } finally {
      setLoading(false);
    }
  };

  if (!tourState) return null;

  return (
    <Card data-testid="guided-tour-settings">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Guided Tour</CardTitle>
        </div>
        <CardDescription>
          A step-by-step walkthrough to help you set up your account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="auto-show-toggle">Show tour automatically</Label>
          <Switch
            id="auto-show-toggle"
            checked={tourState.auto_show_enabled}
            onCheckedChange={() => updateState("toggle_auto_show")}
            disabled={loading}
            data-testid="tour-auto-show-toggle"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => updateState("restart")}
          disabled={loading}
          data-testid="restart-tour-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
          Restart Guided Tour
        </Button>
      </CardContent>
    </Card>
  );
}

function EmailSettingsPanel() {
  const [settings, setSettings] = useState({
    provider_type: 'smtp',
    smtp_host: '',
    smtp_port: 465,
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'ssl',
    resend_api_key: '',
    from_name: '',
    from_email: '',
  });
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await campaignsAPI.getEmailSettings();
        const d = res.data;
        const port = d.smtp_port || 465;
        const encryption = port === 587 ? 'tls' : port === 25 ? 'none' : 'ssl';
        const loaded = {
          provider_type: d.provider_type || 'smtp',
          smtp_host: d.smtp_host || '',
          smtp_port: port,
          smtp_username: d.smtp_username || '',
          smtp_password: d.smtp_password || '',
          smtp_encryption: encryption,
          resend_api_key: d.resend_api_key || '',
          from_name: d.from_name || '',
          from_email: d.from_email || '',
        };
        setSettings(loaded);
        setOriginal(JSON.stringify(loaded));
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const hasChanges = original && JSON.stringify(settings) !== original;

  const handleChange = (field, value) => {
    setSettings(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'smtp_encryption') {
        next.smtp_port = value === 'ssl' ? 465 : value === 'tls' ? 587 : 25;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!settings.from_email) {
      toast.error('From Email is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        provider_type: settings.provider_type,
        from_name: settings.from_name,
        from_email: settings.from_email,
      };
      if (settings.provider_type === 'resend') {
        if (settings.resend_api_key && settings.resend_api_key !== '***') {
          payload.resend_api_key = settings.resend_api_key;
        }
      } else if (settings.provider_type === 'smtp') {
        payload.smtp_host = settings.smtp_host;
        payload.smtp_port = settings.smtp_port;
        payload.smtp_username = settings.smtp_username;
        if (settings.smtp_password && settings.smtp_password !== '***') {
          payload.smtp_password = settings.smtp_password;
        }
      }
      await campaignsAPI.updateEmailSettings(payload);
      const updated = JSON.stringify(settings);
      setOriginal(updated);
      toast.success('Email settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save email settings');
    } finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const res = await campaignsAPI.sendTestEmail();
      toast.success(res.data?.message || 'Test email sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally { setTesting(false); }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Build checklist items based on provider
  const isFilled = (v) => !!v && v.trim() !== '';
  const checklistItems = (() => {
    const common = [
      { label: 'From Name configured', ok: isFilled(settings.from_name) },
      { label: 'From Email configured', ok: isFilled(settings.from_email) },
    ];
    if (settings.provider_type === 'resend') {
      return [{ label: 'Resend API Key configured', ok: isFilled(settings.resend_api_key) }, ...common];
    }
    if (settings.provider_type === 'smtp') {
      return [
        { label: 'SMTP Host configured', ok: isFilled(settings.smtp_host) },
        { label: 'SMTP Port configured', ok: settings.smtp_port > 0 },
        { label: 'SMTP Username configured', ok: isFilled(settings.smtp_username) },
        { label: 'SMTP Password configured', ok: isFilled(settings.smtp_password) },
        { label: 'From Email configured', ok: isFilled(settings.from_email) },
      ];
    }
    // system — only common fields needed
    return common;
  })();
  const allReady = checklistItems.every(i => i.ok);

  return (
    <div className="space-y-6">
      {/* Email Sending Status Card */}
      <Card data-testid="email-status-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Email Sending Status</CardTitle>
            </div>
            <Badge
              variant={allReady ? 'default' : 'outline'}
              className={allReady
                ? 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50'}
              data-testid="email-status-badge"
            >
              {allReady ? 'Ready to Send' : 'Not Fully Configured'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2" data-testid="email-status-checklist">
            {checklistItems.map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                {item.ok
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                <span className={item.ok ? 'text-foreground' : 'text-muted-foreground'}>{item.label}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Email Provider Configuration Card */}
      <Card data-testid="email-settings-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <CardTitle>Email Provider Configuration</CardTitle>
        </div>
        <CardDescription>
          Configure the email provider used for sending marketing campaigns and customer communications.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Provider Selection */}
        <div className="space-y-2">
          <Label>Provider</Label>
          <Select value={settings.provider_type} onValueChange={v => handleChange('provider_type', v)}>
            <SelectTrigger className="w-full max-w-xs" data-testid="email-provider-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="resend">Resend (Recommended for easiest setup)</SelectItem>
              <SelectItem value="smtp">SMTP</SelectItem>
              <SelectItem value="system">System SMTP (fallback)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Resend Fields */}
        {settings.provider_type === 'resend' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground" data-testid="resend-signup-note">
              Need a Resend account? <a href="https://resend.com/signup" target="_blank" rel="noopener noreferrer" className="underline text-primary">Create one here</a>.
            </p>
            <div className="p-4 rounded-lg border bg-muted/30 space-y-2">
            <Label>Resend API Key</Label>
            <div className="relative">
              <Input
                type={showApiKey ? 'text' : 'password'}
                value={settings.resend_api_key}
                onChange={e => handleChange('resend_api_key', e.target.value)}
                placeholder="re_..."
                className="pr-10"
                data-testid="resend-api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(p => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline text-primary">resend.com/api-keys</a>
            </p>
            </div>
          </div>
        )}

        {/* SMTP Fields */}
        {settings.provider_type === 'smtp' && (
          <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SMTP Host</Label>
                <Input
                  value={settings.smtp_host}
                  onChange={e => handleChange('smtp_host', e.target.value)}
                  placeholder="smtp.example.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label>SMTP Port</Label>
                <Input
                  type="number"
                  value={settings.smtp_port}
                  onChange={e => handleChange('smtp_port', parseInt(e.target.value) || 25)}
                  placeholder="465"
                  data-testid="smtp-port-input"
                />
              </div>
              <div className="space-y-2">
                <Label>SMTP Username</Label>
                <Input
                  value={settings.smtp_username}
                  onChange={e => handleChange('smtp_username', e.target.value)}
                  placeholder="user@example.com"
                  data-testid="smtp-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label>SMTP Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={settings.smtp_password}
                    onChange={e => handleChange('smtp_password', e.target.value)}
                    placeholder="Enter password"
                    className="pr-10"
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>SMTP Encryption</Label>
              <Select value={settings.smtp_encryption} onValueChange={v => handleChange('smtp_encryption', v)}>
                <SelectTrigger className="w-full max-w-xs" data-testid="smtp-encryption-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ssl">SSL (port 465)</SelectItem>
                  <SelectItem value="tls">TLS / STARTTLS (port 587)</SelectItem>
                  <SelectItem value="none">None (port 25)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* System SMTP info */}
        {settings.provider_type === 'system' && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <p className="font-medium text-blue-800">System SMTP Fallback</p>
            <p className="text-blue-700 mt-1">
              Emails will be sent using the platform's built-in SMTP relay. No additional configuration is needed.
              Deliverability may be limited compared to a dedicated provider.
            </p>
          </div>
        )}

        {/* Common Fields */}
        <div className="border-t pt-4 space-y-4">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Sender Identity</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From Name</Label>
              <Input
                value={settings.from_name}
                onChange={e => handleChange('from_name', e.target.value)}
                placeholder="Your Company Name"
                data-testid="email-from-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>From Email</Label>
              <Input
                type="email"
                value={settings.from_email}
                onChange={e => handleChange('from_email', e.target.value)}
                placeholder="noreply@yourcompany.com"
                data-testid="email-from-email-input"
              />
              <p className="text-xs text-muted-foreground" data-testid="deliverability-note">
                Use an email address from a verified domain (e.g. hello@yourcompany.com).
                Free mailbox addresses like Gmail or Yahoo may reduce deliverability.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2 border-t">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="gap-1.5 text-xs sm:text-sm shrink-0"
            data-testid="save-email-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : hasChanges ? <Check className="w-4 h-4" /> : null}
            {saving ? 'Saving...' : hasChanges ? 'Save Settings' : 'No Changes'}
          </Button>
          <Button
            variant="outline"
            onClick={handleTestEmail}
            disabled={testing || !settings.from_email}
            className="gap-1.5 text-xs sm:text-sm shrink-0"
            data-testid="send-test-email-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {testing ? 'Sending...' : 'Send Test Email'}
          </Button>
        </div>
      </CardContent>
    </Card>
    </div>
  );
}

export const CompanySettings = () => {
  const { user, isOwner, updateTenantTimezone, hasPerm } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hcpDetailOpen, setHcpDetailOpen] = useState(false);
  const [stDetailOpen, setStDetailOpen] = useState(false);
  const [gbpDetailOpen, setGbpDetailOpen] = useState(false);
  const [hcpStatus, setHcpStatus] = useState(null);
  const canAccessAllSettings = isOwner;
  const tabFromUrl = searchParams.get('tab');
  // Map old tab values to new consolidated tabs for backward compatibility
  const TAB_MIGRATION = {
    general: 'company', branding: 'company',
    notifications: 'communications', email: 'communications', communications: 'communications',
    operations: 'operations', departments: 'operations', tags: 'operations',
    estimates: 'sales', payments: 'sales',
    integrations: 'integrations', migration: 'integrations',
  };
  const resolvedTab = TAB_MIGRATION[tabFromUrl] || tabFromUrl;
  const defaultTab = resolvedTab || (canAccessAllSettings ? 'company' : 'company');
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Sync activeTab when URL search params change (e.g. back/forward navigation, external links)
  useEffect(() => {
    const raw = searchParams.get('tab');
    if (raw) {
      const mapped = TAB_MIGRATION[raw] || raw;
      if (mapped !== activeTab && ['company','communications','operations','sales','integrations'].includes(mapped)) {
        setActiveTab(mapped);
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value }, { replace: true });
  };
  const [saving, setSaving] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(user?.tenant_timezone || 'America/New_York');
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch HCP status for collapsed tile
  useEffect(() => {
    if (activeTab === 'integrations' && !hcpDetailOpen) {
      housecallProAPI.getStatus().then(r => setHcpStatus(r.data)).catch(() => {});
    }
  }, [activeTab, hcpDetailOpen]);
  
  // Branding state
  const [forceLogoEnabled, setForceLogoEnabled] = useState(false);
  const [selectedForcedLogo, setSelectedForcedLogo] = useState('');
  const [brandingKey, setBrandingKey] = useState(0); // Key for forcing re-render
  
  // Company branding state
  const [companyBranding, setCompanyBranding] = useState({
    company_name: '',
    company_logo_url: '',
    company_logo_storage_path: '',
    company_phone: '',
    company_email: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    invoice_header_bg_color: '',
    invoice_header_text_color: '',
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [savingBranding, setSavingBranding] = useState(false);
  const [brandingChanged, setBrandingChanged] = useState(false);
  
  // i18n Settings state
  const [i18nSettings, setI18nSettings] = useState({
    business_country_code: 'US',
    industry_code: '',
    industry_other_text: '',
    currency_code: 'USD',
    service_country_codes: ['US'],
  });
  const [i18nOriginal, setI18nOriginal] = useState(null);
  const [loadingI18n, setLoadingI18n] = useState(true);
  const [savingI18n, setSavingI18n] = useState(false);
  
  // PO Departments state (Phase 4)
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [departmentName, setDepartmentName] = useState('');
  const [departmentSpendLimit, setDepartmentSpendLimit] = useState(''); // Phase 7A
  const [departmentHasSpendLimit, setDepartmentHasSpendLimit] = useState(false); // Phase 7A
  const [savingDepartment, setSavingDepartment] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingDepartment, setDeletingDepartment] = useState(null);
  
  // PO Notification Settings state (Phase 3 - Email Notifications)
  const [poNotificationSettings, setPoNotificationSettings] = useState({
    po_received_email_to: '',
    po_received_email_cc: '',
    require_job_number_for_po_received_email: true,
    po_received_notify_mode: 'fully_received'
  });
  const [loadingNotificationSettings, setLoadingNotificationSettings] = useState(true);
  const [savingNotificationSettings, setSavingNotificationSettings] = useState(false);
  const [notificationSettingsChanged, setNotificationSettingsChanged] = useState(false);
  const [notificationValidationError, setNotificationValidationError] = useState(null);
  
  // Notification Engine state (Event-based notifications)
  const [notifEngineSettings, setNotifEngineSettings] = useState(null);
  const [notifEngineError, setNotifEngineError] = useState(null);

  // Daily Digest state
  const [digestSettings, setDigestSettings] = useState({
    daily_digest_enabled: false,
    daily_digest_send_time_local: '07:00',
    daily_digest_include_warehouse_emails: false,
    tenant_timezone: 'America/Chicago',
  });
  const [digestOriginal, setDigestOriginal] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestPreviewLoading, setDigestPreviewLoading] = useState(false);
  const [notifEngineOriginal, setNotifEngineOriginal] = useState(null);
  const [loadingNotifEngine, setLoadingNotifEngine] = useState(true);
  const [savingNotifEngine, setSavingNotifEngine] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Stage 20: Operations settings state
  const [opsAlertTime, setOpsAlertTime] = useState('06:00');
  const [opsTimezone, setOpsTimezone] = useState('');
  const [dayBeforeEnabled, setDayBeforeEnabled] = useState(false);
  const [dayBeforeTime, setDayBeforeTime] = useState('17:00');
  const [readinessGoalPct, setReadinessGoalPct] = useState(90);
  const [loadingOps, setLoadingOps] = useState(true);
  const [savingOps, setSavingOps] = useState(false);
  
  // Estimate recommendation text defaults
  const [estRecDefaults, setEstRecDefaults] = useState({ estimate_rec_text_good: '', estimate_rec_text_better: '', estimate_rec_text_best: '' });
  const [estRecOriginal, setEstRecOriginal] = useState({ estimate_rec_text_good: '', estimate_rec_text_better: '', estimate_rec_text_best: '' });
  const [loadingEstRec, setLoadingEstRec] = useState(true);
  const [savingEstRec, setSavingEstRec] = useState(false);

  // Estimate appointment reminder setting
  const [estimateReminderEnabled, setEstimateReminderEnabled] = useState(false);
  const [savingEstReminder, setSavingEstReminder] = useState(false);
  
  // Default tax rate state
  const [defaultTaxRate, setDefaultTaxRate] = useState('');
  const [defaultTaxRateOriginal, setDefaultTaxRateOriginal] = useState('');
  const [savingTaxRate, setSavingTaxRate] = useState(false);
  
  // Load branding override state on mount
  useEffect(() => {
    const override = getForceLogoOverride();
    setForceLogoEnabled(override.enabled);
    setSelectedForcedLogo(override.logoUrl || '');
  }, []);

  // Stage 20: Load operations settings
  useEffect(() => {
    const loadOps = async () => {
      try {
        const res = await api.get('/company-settings/operations');
        setOpsAlertTime(res.data.truck_prep_alert_time || '06:00');
        setOpsTimezone(res.data.business_timezone || '');
        setDayBeforeEnabled(res.data.day_before_alert_enabled || false);
        setDayBeforeTime(res.data.day_before_alert_time || '17:00');
        setReadinessGoalPct(res.data.readiness_goal_pct ?? 90);
      } catch {
        // Silently fail for non-owner/office
      } finally {
        setLoadingOps(false);
      }
    };
    loadOps();
  }, []);

  // Load estimate recommendation text defaults
  useEffect(() => {
    const loadEstRec = async () => {
      try {
        const res = await estimatesAPI.getDefaults();
        const d = {
          estimate_rec_text_good: res.data.estimate_rec_text_good || '',
          estimate_rec_text_better: res.data.estimate_rec_text_better || '',
          estimate_rec_text_best: res.data.estimate_rec_text_best || '',
        };
        setEstRecDefaults(d);
        setEstRecOriginal(d);
        const taxVal = res.data.default_tax_rate != null ? String(res.data.default_tax_rate) : '';
        setDefaultTaxRate(taxVal);
        setDefaultTaxRateOriginal(taxVal);
      } catch { /* silently fail for non-owner */ }
      finally { setLoadingEstRec(false); }
    };
    loadEstRec();
  }, []);

  const saveEstRecDefaults = async () => {
    setSavingEstRec(true);
    try {
      const res = await estimatesAPI.updateDefaults(estRecDefaults);
      const d = {
        estimate_rec_text_good: res.data.estimate_rec_text_good || '',
        estimate_rec_text_better: res.data.estimate_rec_text_better || '',
        estimate_rec_text_best: res.data.estimate_rec_text_best || '',
      };
      setEstRecDefaults(d);
      setEstRecOriginal(d);
      toast.success('Estimate defaults saved');
    } catch { toast.error('Failed to save estimate defaults'); }
    finally { setSavingEstRec(false); }
  };

  const estRecChanged = JSON.stringify(estRecDefaults) !== JSON.stringify(estRecOriginal);

  // Load estimate appointment reminder setting + default payment methods
  const [defaultPaymentMethods, setDefaultPaymentMethods] = useState({ card: true, cash: true, check: true, online: true, ach: false });
  const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
  useEffect(() => {
    settingsAPI.get().then(res => {
      setEstimateReminderEnabled(res.data.estimate_appointment_reminder_enabled || false);
      if (res.data.default_allowed_payment_methods) {
        setDefaultPaymentMethods(res.data.default_allowed_payment_methods);
      }
    }).catch(() => {});
  }, []);

  const toggleEstimateReminder = async (val) => {
    setSavingEstReminder(true);
    try {
      const res = await settingsAPI.update({ estimate_appointment_reminder_enabled: val });
      setEstimateReminderEnabled(res.data.estimate_appointment_reminder_enabled);
      toast.success(val ? 'Estimate appointment reminders enabled' : 'Estimate appointment reminders disabled');
    } catch { toast.error('Failed to update setting'); }
    finally { setSavingEstReminder(false); }
  };

  const toggleDefaultPaymentMethod = async (key) => {
    const next = { ...defaultPaymentMethods, [key]: !defaultPaymentMethods[key] };
    // When online is toggled OFF, also disable ach
    if (key === 'online' && !next.online) {
      next.ach = false;
    }
    if (!Object.values(next).some(Boolean)) {
      toast.error('At least one payment method must be enabled');
      return;
    }
    setSavingPaymentMethods(true);
    try {
      const res = await settingsAPI.update({ default_allowed_payment_methods: next });
      setDefaultPaymentMethods(res.data.default_allowed_payment_methods || next);
      toast.success('Default payment methods updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment methods');
    } finally {
      setSavingPaymentMethods(false);
    }
  };

  const taxRateChanged = defaultTaxRate !== defaultTaxRateOriginal;

  const saveTaxRate = async () => {
    const parsed = defaultTaxRate === '' ? null : parseFloat(defaultTaxRate);
    if (parsed !== null && (isNaN(parsed) || parsed < 0 || parsed > 100)) {
      toast.error('Tax rate must be between 0 and 100');
      return;
    }
    setSavingTaxRate(true);
    try {
      const res = await estimatesAPI.updateDefaults({ default_tax_rate: parsed === null ? -1 : parsed });
      const taxVal = res.data.default_tax_rate != null ? String(res.data.default_tax_rate) : '';
      setDefaultTaxRate(taxVal);
      setDefaultTaxRateOriginal(taxVal);
      toast.success('Default tax rate saved');
    } catch { toast.error('Failed to save tax rate'); }
    finally { setSavingTaxRate(false); }
  };
  
  // Load company branding on mount
  useEffect(() => {
    const loadCompanyBranding = async () => {
      try {
        const response = await warehouseAPI.getCompanyBranding();
        setCompanyBranding({
          company_name: response.data.company_name || '',
          company_logo_url: response.data.company_logo_url || '',
          company_logo_storage_path: response.data.company_logo_storage_path || '',
          company_phone: response.data.company_phone || '',
          company_email: response.data.company_email || '',
          billing_address_line1: response.data.billing_address_line1 || '',
          billing_address_line2: response.data.billing_address_line2 || '',
          billing_city: response.data.billing_city || '',
          billing_state: response.data.billing_state || '',
          billing_postal_code: response.data.billing_postal_code || '',
          invoice_header_bg_color: response.data.invoice_header_bg_color || '',
          invoice_header_text_color: response.data.invoice_header_text_color || '',
        });
      } catch (error) {
        console.error('Failed to load company branding:', error);
      } finally {
        setLoadingBranding(false);
      }
    };
    loadCompanyBranding();
  }, []);
  
  // Load i18n settings on mount
  useEffect(() => {
    const loadI18n = async () => {
      try {
        const res = await i18nSettingsAPI.get();
        const d = res.data;
        const state = {
          business_country_code: d.business_country_code || 'US',
          industry_code: d.industry_code || '',
          industry_other_text: d.industry_other_text || '',
          currency_code: d.currency_code || 'USD',
          service_country_codes: Array.isArray(d.service_country_codes) && d.service_country_codes.length > 0
            ? d.service_country_codes
            : [d.business_country_code || 'US'],
        };
        setI18nSettings(state);
        setI18nOriginal(JSON.stringify(state));
      } catch (e) {
        console.error('Failed to load i18n settings:', e);
      } finally {
        setLoadingI18n(false);
      }
    };
    loadI18n();
  }, []);
  
  // Load PO departments on mount (Phase 4)
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await purchaseOrdersAPI.listDepartments(true); // Include inactive
        setDepartments(response.data);
      } catch (error) {
        console.error('Failed to load departments:', error);
      } finally {
        setLoadingDepartments(false);
      }
    };
    loadDepartments();
  }, []);
  
  // Load PO notification settings on mount (Phase 3 - Email Notifications)
  useEffect(() => {
    const loadNotificationSettings = async () => {
      try {
        const response = await poNotificationSettingsAPI.get();
        setPoNotificationSettings({
          po_received_email_to: response.data.po_received_email_to || '',
          po_received_email_cc: response.data.po_received_email_cc || '',
          require_job_number_for_po_received_email: response.data.require_job_number_for_po_received_email !== false,
          po_received_notify_mode: response.data.po_received_notify_mode || 'fully_received'
        });
      } catch (error) {
        console.error('Failed to load PO notification settings:', error);
      } finally {
        setLoadingNotificationSettings(false);
      }
    };
    loadNotificationSettings();
  }, []);
  
  // Load Notification Engine settings on mount
  const loadNotifEngine = React.useCallback(async () => {
    setLoadingNotifEngine(true);
    setNotifEngineError(null);
    try {
      const response = await notificationSettingsAPI.get();
      setNotifEngineSettings(response.data);
      setNotifEngineOriginal(JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to load notification engine settings:', error);
      setNotifEngineError(error.response?.data?.detail || 'Failed to load notification settings');
    } finally {
      setLoadingNotifEngine(false);
    }
  }, []);

  useEffect(() => {
    loadNotifEngine();
  }, [loadNotifEngine]);
  
  // Load daily digest settings
  useEffect(() => {
    const loadDigest = async () => {
      setDigestLoading(true);
      try {
        const res = await dailyDigestAPI.getSettings();
        setDigestSettings(res.data);
        setDigestOriginal(JSON.stringify(res.data));
      } catch (err) {
        console.error('Failed to load digest settings:', err);
      } finally {
        setDigestLoading(false);
      }
    };
    if (isOwner) loadDigest();
  }, [isOwner]);

  // Get current active logo info
  const currentLogoInfo = getActiveLogo(BRANDING_CONFIG, new Date(), user?.tenant_timezone);
  const availableLogos = getAvailableLogos();
  
  // Company branding handlers
  const handleBrandingChange = (field, value) => {
    setCompanyBranding(prev => ({ ...prev, [field]: value }));
    setBrandingChanged(true);
  };
  
  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      // Don't send storage_path via the branding update — it's managed by upload/delete
      const { company_logo_storage_path, ...brandingPayload } = companyBranding;
      await warehouseAPI.updateCompanyBranding(brandingPayload);
      setBrandingChanged(false);
      toast.success('Company info saved', {
        description: 'Your company branding has been updated.'
      });
    } catch (error) {
      toast.error('Failed to save', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setSavingBranding(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/company-branding/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCompanyBranding(prev => ({ ...prev, company_logo_storage_path: res.data.storage_path }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleRemoveUploadedLogo = async () => {
    try {
      await api.delete('/company-branding/upload-logo');
      setCompanyBranding(prev => ({ ...prev, company_logo_storage_path: '' }));
      toast.success('Uploaded logo removed');
    } catch {
      toast.error('Failed to remove logo');
    }
  };
  
  // i18n settings handlers
  const handleI18nChange = (field, value) => {
    setI18nSettings(prev => {
      const next = { ...prev, [field]: value };
      // Auto-update currency when country changes
      if (field === 'business_country_code' && COUNTRY_CURRENCY_MAP[value]) {
        next.currency_code = COUNTRY_CURRENCY_MAP[value];
      }
      // Ensure the primary country is always included in service countries.
      if (field === 'business_country_code') {
        const sc = Array.isArray(prev.service_country_codes) ? prev.service_country_codes : [];
        next.service_country_codes = sc.includes(value) ? sc : [value, ...sc];
      }
      // Clear other text when industry changes away from 'other'
      if (field === 'industry_code' && value !== 'other') {
        next.industry_other_text = '';
      }
      return next;
    });
  };

  const handleToggleServiceCountry = (code) => {
    setI18nSettings(prev => {
      const sc = Array.isArray(prev.service_country_codes) ? [...prev.service_country_codes] : [];
      const isPrimary = code === prev.business_country_code;
      // Primary country cannot be removed.
      if (isPrimary) {
        if (!sc.includes(code)) sc.push(code);
        return { ...prev, service_country_codes: sc };
      }
      const idx = sc.indexOf(code);
      if (idx >= 0) sc.splice(idx, 1);
      else sc.push(code);
      return { ...prev, service_country_codes: sc };
    });
  };

  const i18nHasChanges = i18nOriginal !== null && JSON.stringify(i18nSettings) !== i18nOriginal;

  const handleSaveI18n = async () => {
    setSavingI18n(true);
    try {
      await i18nSettingsAPI.update({
        business_country_code: i18nSettings.business_country_code,
        industry_code: i18nSettings.industry_code || null,
        industry_other_text: i18nSettings.industry_code === 'other' ? (i18nSettings.industry_other_text || null) : null,
        currency_code: i18nSettings.currency_code,
        service_country_codes: Array.isArray(i18nSettings.service_country_codes)
          ? i18nSettings.service_country_codes
          : [i18nSettings.business_country_code],
      });
      setI18nOriginal(JSON.stringify(i18nSettings));
      toast.success('Business settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save business settings');
    } finally {
      setSavingI18n(false);
    }
  };
  
  // PO Department handlers (Phase 4)
  const openAddDepartment = () => {
    setEditingDepartment(null);
    setDepartmentName('');
    setDepartmentSpendLimit(''); // Phase 7A
    setDepartmentHasSpendLimit(false); // Phase 7A
    setDepartmentModalOpen(true);
  };
  
  const openEditDepartment = (dept) => {
    setEditingDepartment(dept);
    setDepartmentName(dept.name);
    // Phase 7A: Load spend limit
    if (dept.spend_limit_cents !== null && dept.spend_limit_cents !== undefined) {
      setDepartmentHasSpendLimit(true);
      setDepartmentSpendLimit((dept.spend_limit_cents / 100).toString());
    } else {
      setDepartmentHasSpendLimit(false);
      setDepartmentSpendLimit('');
    }
    setDepartmentModalOpen(true);
  };
  
  const handleSaveDepartment = async () => {
    if (!departmentName.trim()) {
      toast.error('Department name is required');
      return;
    }
    
    // Phase 7A: Validate spend limit
    let spendLimitCents = null;
    if (departmentHasSpendLimit) {
      const limitValue = parseFloat(departmentSpendLimit);
      if (isNaN(limitValue) || limitValue < 0) {
        toast.error('Please enter a valid spend limit (0 or higher)');
        return;
      }
      spendLimitCents = Math.round(limitValue * 100);
    }
    
    setSavingDepartment(true);
    try {
      if (editingDepartment) {
        await purchaseOrdersAPI.updateDepartment(editingDepartment.id, { 
          name: departmentName.trim(),
          spend_limit_cents: spendLimitCents
        });
        toast.success('Department updated');
      } else {
        await purchaseOrdersAPI.createDepartment({ 
          name: departmentName.trim(),
          spend_limit_cents: spendLimitCents
        });
        toast.success('Department created');
      }
      setDepartmentModalOpen(false);
      const response = await purchaseOrdersAPI.listDepartments(true);
      setDepartments(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save department');
    } finally {
      setSavingDepartment(false);
    }
  };
  
  const toggleDepartmentActive = async (dept) => {
    try {
      await purchaseOrdersAPI.updateDepartment(dept.id, { is_active: !dept.is_active });
      const response = await purchaseOrdersAPI.listDepartments(true);
      setDepartments(response.data);
      toast.success(dept.is_active ? 'Department deactivated' : 'Department activated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update department');
    }
  };
  
  const handleDeleteDepartment = async () => {
    if (!deletingDepartment) return;
    try {
      await purchaseOrdersAPI.deleteDepartment(deletingDepartment.id);
      toast.success('Department deleted');
      setDeleteConfirmOpen(false);
      setDeletingDepartment(null);
      const response = await purchaseOrdersAPI.listDepartments(true);
      setDepartments(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete department');
    }
  };
  
  // PO Notification Settings handlers (Phase 3 - Email Notifications)
  const handleNotificationSettingChange = (field, value) => {
    setPoNotificationSettings(prev => ({ ...prev, [field]: value }));
    setNotificationSettingsChanged(true);
    setNotificationValidationError(null);
  };
  
  // Simple email validation regex
  const isValidEmail = (email) => {
    if (!email || !email.trim()) return true; // Empty is valid
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim());
  };
  
  // Validate comma-separated email list
  const validateEmailList = (emailString) => {
    if (!emailString || !emailString.trim()) return { valid: true, invalid: [] };
    const emails = emailString.split(',').map(e => e.trim()).filter(e => e);
    const invalid = emails.filter(e => !isValidEmail(e));
    return { valid: invalid.length === 0, invalid };
  };
  
  const handleSaveNotificationSettings = async () => {
    // Validate emails
    const toValidation = validateEmailList(poNotificationSettings.po_received_email_to);
    const ccValidation = validateEmailList(poNotificationSettings.po_received_email_cc);
    
    if (!toValidation.valid) {
      setNotificationValidationError(`Invalid email(s) in To field: ${toValidation.invalid.join(', ')}`);
      return;
    }
    if (!ccValidation.valid) {
      setNotificationValidationError(`Invalid email(s) in CC field: ${ccValidation.invalid.join(', ')}`);
      return;
    }
    
    setSavingNotificationSettings(true);
    setNotificationValidationError(null);
    try {
      await poNotificationSettingsAPI.update({
        po_received_email_to: poNotificationSettings.po_received_email_to || null,
        po_received_email_cc: poNotificationSettings.po_received_email_cc || null,
        require_job_number_for_po_received_email: poNotificationSettings.require_job_number_for_po_received_email,
        po_received_notify_mode: poNotificationSettings.po_received_notify_mode
      });
      setNotificationSettingsChanged(false);
      toast.success('Notification settings saved', {
        description: 'PO fully received email notifications updated.'
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        setNotificationValidationError(detail);
      }
      toast.error('Failed to save', {
        description: detail || 'Please check your input and try again.'
      });
    } finally {
      setSavingNotificationSettings(false);
    }
  };

  // Notification Engine handlers
  const notifEngineChanged = notifEngineSettings && JSON.stringify(notifEngineSettings) !== notifEngineOriginal;

  const digestChanged = digestOriginal && JSON.stringify(digestSettings) !== digestOriginal;

  const handleDigestChange = (field, value) => {
    setDigestSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDigest = async () => {
    setDigestSaving(true);
    try {
      const res = await dailyDigestAPI.updateSettings(digestSettings);
      setDigestSettings(res.data);
      setDigestOriginal(JSON.stringify(res.data));
      toast.success('Daily digest settings saved');
    } catch (err) {
      toast.error('Failed to save', { description: err.response?.data?.detail || 'Try again.' });
    } finally {
      setDigestSaving(false);
    }
  };

  const handlePreviewDigest = async () => {
    setDigestPreviewLoading(true);
    try {
      const res = await dailyDigestAPI.preview();
      setDigestPreview(res.data);
    } catch (err) {
      toast.error('Failed to load preview', { description: err.response?.data?.detail });
    } finally {
      setDigestPreviewLoading(false);
    }
  };
  
  const handleNotifEngineGlobalChange = (field, value) => {
    setNotifEngineSettings(prev => ({ ...prev, [field]: value }));
  };
  
  const handleNotifEngineRuleChange = (eventKey, field, value) => {
    setNotifEngineSettings(prev => ({
      ...prev,
      rules: prev.rules.map(r => r.event_key === eventKey ? { ...r, [field]: value } : r),
    }));
  };
  
  const handleSaveNotifEngine = async () => {
    setSavingNotifEngine(true);
    try {
      const response = await notificationSettingsAPI.update(notifEngineSettings);
      setNotifEngineSettings(response.data);
      setNotifEngineOriginal(JSON.stringify(response.data));
      toast.success('Event notification settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notification settings');
    } finally {
      setSavingNotifEngine(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      await notificationSettingsAPI.sendTestEmail();
      toast.success('Test email sent.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      if (status === 400 || status === 429) {
        toast.warning(detail);
      } else {
        toast.error(detail || 'Failed to send test email');
      }
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleTimezoneChange = (value) => {
    setSelectedTimezone(value);
    setHasChanges(value !== user?.tenant_timezone);
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      await updateTenantTimezone(selectedTimezone);
      setHasChanges(false);
      toast.success('Business timezone updated', {
        description: 'All audit timestamps will now display in the new timezone.'
      });
    } catch (error) {
      toast.error('Failed to update timezone', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setSaving(false);
    }
  };

  // Stage 20: Save operations settings
  const handleSaveOps = async () => {
    setSavingOps(true);
    try {
      const res = await api.put('/company-settings/operations', {
        truck_prep_alert_time: opsAlertTime,
        day_before_alert_enabled: dayBeforeEnabled,
        day_before_alert_time: dayBeforeTime,
        readiness_goal_pct: readinessGoalPct,
      });
      setOpsAlertTime(res.data.truck_prep_alert_time);
      setDayBeforeEnabled(res.data.day_before_alert_enabled);
      setDayBeforeTime(res.data.day_before_alert_time);
      setReadinessGoalPct(res.data.readiness_goal_pct ?? 90);
      toast.success('Operations settings updated');
    } catch (error) {
      toast.error('Failed to save', { description: error.response?.data?.detail?.[0]?.msg || 'Invalid time format' });
    } finally {
      setSavingOps(false);
    }
  };

  
  // Branding handlers
  const handleForceLogoToggle = (enabled) => {
    setForceLogoEnabled(enabled);
    if (enabled && selectedForcedLogo) {
      setForceLogoOverride(true, selectedForcedLogo);
      setBrandingKey(prev => prev + 1);
      toast.success('Logo override enabled', {
        description: 'The selected logo will be displayed across the app.'
      });
    } else {
      setForceLogoOverride(false, null);
      setBrandingKey(prev => prev + 1);
      if (!enabled) {
        toast.success('Logo override disabled', {
          description: 'Seasonal logo switching is now active.'
        });
      }
    }
  };
  
  const handleForcedLogoChange = (logoUrl) => {
    setSelectedForcedLogo(logoUrl);
    if (forceLogoEnabled) {
      setForceLogoOverride(true, logoUrl);
      setBrandingKey(prev => prev + 1);
      toast.success('Logo updated', {
        description: 'The new logo is now being displayed.'
      });
    }
  };

  // Get current time in selected timezone for preview
  const getCurrentTimeInTimezone = () => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: selectedTimezone,
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
      return formatter.format(now);
    } catch {
      return '';
    }
  };

  // Find the label for the current timezone
  const getTimezoneLabel = (tz) => {
    for (const group of TIMEZONE_OPTIONS) {
      const found = group.timezones.find(t => t.value === tz);
      if (found) return found.label;
    }
    return tz;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header - matches Warehouse/Audit layout */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Company Settings</h1>
              <p className="text-sm text-muted-foreground">
                Configure settings that apply to your entire organization
              </p>
            </div>
          </div>
        </div>

        {/* Tabs for different settings sections */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="w-full justify-start h-10">
              <TabsTrigger value="company" className="gap-1.5 text-xs sm:text-sm" data-testid="settings-tab-company">
                <Building2 className="w-4 h-4" />
                Company
              </TabsTrigger>
            {canAccessAllSettings && (
              <TabsTrigger value="communications" className="gap-1.5 text-xs sm:text-sm" data-testid="settings-tab-communications">
                <MessageSquare className="w-4 h-4" />
                Communications
              </TabsTrigger>
            )}
            {canAccessAllSettings && (
              <TabsTrigger value="operations" className="gap-1.5 text-xs sm:text-sm" data-testid="settings-tab-operations">
                <Wrench className="w-4 h-4" />
                Operations
              </TabsTrigger>
            )}
            {canAccessAllSettings && (
              <TabsTrigger value="sales" className="gap-1.5 text-xs sm:text-sm" data-testid="settings-tab-sales">
                <CreditCard className="w-4 h-4" />
                Sales
              </TabsTrigger>
            )}
            {canAccessAllSettings && (
              <TabsTrigger value="integrations" className="gap-1.5 text-xs sm:text-sm" data-testid="settings-tab-integrations">
                <Zap className="w-4 h-4" />
                Integrations
              </TabsTrigger>
            )}
          </TabsList>

          {/* ====== COMPANY TAB ====== */}
          <TabsContent value="company" className="space-y-6">
            {/* --- General Settings Section --- */}
            <h3 className="text-lg font-semibold" data-testid="section-general-settings">General Settings</h3>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <CardTitle>Business Timezone</CardTitle>
                </div>
                <CardDescription>
                  This timezone is used for all audit timestamps and accountability displays. 
                  All team members will see the same times.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!isOwner ? (
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      Only the account owner can change the business timezone.
                    </p>
                    <p className="text-sm mt-2">
                      Current timezone: <span className="font-medium">{getTimezoneLabel(user?.tenant_timezone)}</span>
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="timezone">Timezone</Label>
                      <Select value={selectedTimezone} onValueChange={handleTimezoneChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONE_OPTIONS.map((group) => (
                            <React.Fragment key={group.group}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                                {group.group}
                              </div>
                              {group.timezones.map((tz) => (
                                <SelectItem key={tz.value} value={tz.value}>
                                  <div className="flex items-center gap-2">
                                    <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                                    {tz.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </React.Fragment>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Live preview */}
                    <div className="p-4 bg-muted/50 rounded-lg border">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Clock className="w-4 h-4" />
                        Current time in {getTimezoneLabel(selectedTimezone)}:
                      </div>
                      <p className="text-lg font-mono font-medium">{getCurrentTimeInTimezone()}</p>
                    </div>

                    {/* Info box */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                      <p className="text-blue-800">
                        <strong>How this affects your team:</strong>
                      </p>
                      <ul className="mt-2 text-blue-700 space-y-1">
                        <li>• All audit timestamps will display in this timezone</li>
                        <li>• Every team member sees the same time for accountability</li>
                        <li>• Last Audited indicators use this timezone</li>
                        <li>• Stored data remains in UTC — only display is affected</li>
                      </ul>
                    </div>

                    <div className="flex justify-end">
                      <Button 
                        onClick={handleSave} 
                        disabled={!hasChanges || saving}
                        className="gap-1.5 text-xs sm:text-sm shrink-0"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : hasChanges ? (
                          <Check className="w-4 h-4" />
                        ) : null}
                        {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* --- Business / Internationalization Settings --- */}
            <Card data-testid="i18n-settings-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  <CardTitle>Business Location & Industry</CardTitle>
                </div>
                <CardDescription>
                  Your business country determines address formats, currency defaults, and autocomplete regions.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingI18n ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : !isOwner ? (
                  <div className="p-4 bg-muted rounded-lg text-sm text-muted-foreground">
                    Only the account owner can change business settings.
                    <div className="mt-2 space-y-1">
                      <p>Country: <span className="font-medium text-foreground">{getCountryName(i18nSettings.business_country_code)}</span></p>
                      <p>Industry: <span className="font-medium text-foreground">{i18nSettings.industry_code ? getIndustryLabel(i18nSettings.industry_code) : '—'}</span></p>
                      <p>Currency: <span className="font-medium text-foreground">{i18nSettings.currency_code}</span></p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Business Country</Label>
                        <Select
                          value={i18nSettings.business_country_code}
                          onValueChange={(v) => handleI18nChange('business_country_code', v)}
                        >
                          <SelectTrigger data-testid="i18n-country-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map(c => (
                              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select
                          value={i18nSettings.currency_code}
                          onValueChange={(v) => handleI18nChange('currency_code', v)}
                        >
                          <SelectTrigger data-testid="i18n-currency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCIES.map(c => (
                              <SelectItem key={c.code} value={c.code}>{c.symbol} {c.name} ({c.code})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Currency is auto-set when you change your business country. Override if needed.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Industry</Label>
                      <Select
                        value={i18nSettings.industry_code}
                        onValueChange={(v) => handleI18nChange('industry_code', v)}
                      >
                        <SelectTrigger data-testid="i18n-industry-select">
                          <SelectValue placeholder="Select industry..." />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRIES.map(i => (
                            <SelectItem key={i.code} value={i.code}>{i.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {i18nSettings.industry_code === 'other' && (
                      <div className="space-y-2">
                        <Label>Please specify your industry</Label>
                        <Input
                          value={i18nSettings.industry_other_text}
                          onChange={(e) => handleI18nChange('industry_other_text', e.target.value)}
                          placeholder="e.g. Marine Services"
                          data-testid="i18n-industry-other-input"
                        />
                      </div>
                    )}

                    {/* Service Countries — multi-country support */}
                    <div className="space-y-2 pt-2 border-t">
                      <Label>Service Countries</Label>
                      <p className="text-xs text-muted-foreground">
                        Countries you serve customers in. Address autocomplete will return results for any selected country. Currency still defaults from your primary business country.
                      </p>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {COUNTRIES.map(c => {
                          const checked = (i18nSettings.service_country_codes || []).includes(c.code);
                          const isPrimary = c.code === i18nSettings.business_country_code;
                          return (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => handleToggleServiceCountry(c.code)}
                              disabled={isPrimary}
                              data-testid={`service-country-${c.code}`}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                checked
                                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                                  : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                              } ${isPrimary ? 'opacity-90 cursor-default' : 'cursor-pointer'}`}
                              title={isPrimary ? 'Primary business country (always included)' : ''}
                            >
                              {c.name}{isPrimary ? ' (primary)' : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={handleSaveI18n}
                        disabled={!i18nHasChanges || savingI18n}
                        className="gap-1.5 text-xs sm:text-sm shrink-0"
                        data-testid="save-i18n-button"
                      >
                        {savingI18n ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : i18nHasChanges ? (
                          <Check className="w-4 h-4" />
                        ) : null}
                        {savingI18n ? 'Saving...' : i18nHasChanges ? 'Save Changes' : 'No Changes'}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Guided Tour Card */}
            <GuidedTourSettings />

            {/* --- Branding Section --- */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4" data-testid="section-branding">Branding & Appearance</h3>
            </div>
            {/* Company Info Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  <CardTitle>Company Info</CardTitle>
                </div>
                <CardDescription>
                  This information appears on printable Purchase Orders and vendor communications.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingBranding ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {/* Company Name */}
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Company Name</Label>
                      <Input
                        id="company_name"
                        value={companyBranding.company_name}
                        onChange={(e) => handleBrandingChange('company_name', e.target.value)}
                        placeholder="Your Company Name"
                        data-testid="company-name-input"
                      />
                    </div>

                    {/* ── Company Logo ── */}
                    <div className="space-y-3 pt-4 border-t">
                      <Label className="text-sm font-medium">Company Logo</Label>
                      <p className="text-xs text-muted-foreground -mt-1">
                        Used on estimate PDFs and branded documents. Upload a file or paste a URL.
                      </p>

                      {/* Active logo preview */}
                      {(companyBranding.company_logo_storage_path || companyBranding.company_logo_url) && (
                        <div className="flex items-center gap-4 p-3 border rounded-lg bg-muted/30" data-testid="active-logo-preview">
                          <img
                            src={companyBranding.company_logo_storage_path
                              ? `${process.env.REACT_APP_BACKEND_URL}/api/storage/${companyBranding.company_logo_storage_path}`
                              : companyBranding.company_logo_url}
                            alt="Company logo"
                            className="h-12 w-auto max-w-[160px] object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                            data-testid="active-logo-img"
                          />
                          <div className="text-xs text-muted-foreground">
                            {companyBranding.company_logo_storage_path
                              ? <span className="inline-flex items-center gap-1 text-green-700 font-medium"><Check className="w-3 h-3" /> Uploaded file</span>
                              : <span className="inline-flex items-center gap-1 text-blue-700 font-medium"><Globe className="w-3 h-3" /> From URL</span>
                            }
                          </div>
                        </div>
                      )}

                      {/* Upload section */}
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-xs"
                          disabled={uploadingLogo}
                          onClick={() => document.getElementById('logo-upload-input').click()}
                          data-testid="upload-logo-btn"
                        >
                          {uploadingLogo
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Upload className="w-3.5 h-3.5" />}
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </Button>
                        <input
                          id="logo-upload-input"
                          type="file"
                          accept=".png,.jpg,.jpeg,.webp"
                          className="hidden"
                          onChange={handleLogoUpload}
                          data-testid="logo-upload-file-input"
                        />
                        {companyBranding.company_logo_storage_path && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-destructive hover:text-destructive gap-1.5"
                            onClick={handleRemoveUploadedLogo}
                            data-testid="remove-uploaded-logo-btn"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Remove Upload
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        PNG, JPG, or WebP. Max 2 MB. Uploaded logo takes priority over Logo URL.
                      </p>

                      {/* Logo URL fallback */}
                      <div className="space-y-2 pt-2">
                        <Label htmlFor="company_logo_url" className="text-xs text-muted-foreground">Logo URL (fallback)</Label>
                        <Input
                          id="company_logo_url"
                          value={companyBranding.company_logo_url}
                          onChange={(e) => handleBrandingChange('company_logo_url', e.target.value)}
                          placeholder="https://example.com/logo.png"
                          className="text-xs"
                          data-testid="company-logo-url-input"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Used only if no uploaded logo exists.
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="company_phone" className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> Phone
                        </Label>
                        <Input
                          id="company_phone"
                          value={companyBranding.company_phone}
                          onChange={(e) => handleBrandingChange('company_phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          data-testid="company-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company_email" className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email
                        </Label>
                        <Input
                          id="company_email"
                          type="email"
                          value={companyBranding.company_email}
                          onChange={(e) => handleBrandingChange('company_email', e.target.value)}
                          placeholder="orders@company.com"
                          data-testid="company-email-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          This email will be used as the Reply-To address when emailing POs to vendors.
                        </p>
                      </div>
                    </div>
                    
                    {/* Billing/Remit-to Address */}
                    <div className="space-y-4 pt-4 border-t">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <MapPin className="w-4 h-4" />
                        Billing / Remit-to Address (Optional)
                      </div>
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="billing_address_line1">Address Line 1</Label>
                          <Input
                            id="billing_address_line1"
                            value={companyBranding.billing_address_line1}
                            onChange={(e) => handleBrandingChange('billing_address_line1', e.target.value)}
                            placeholder="123 Main Street"
                            data-testid="billing-address-line1-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="billing_address_line2">Address Line 2</Label>
                          <Input
                            id="billing_address_line2"
                            value={companyBranding.billing_address_line2}
                            onChange={(e) => handleBrandingChange('billing_address_line2', e.target.value)}
                            placeholder="Suite 100"
                            data-testid="billing-address-line2-input"
                          />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-3">
                          <div className="space-y-2">
                            <Label htmlFor="billing_city">City</Label>
                            <Input
                              id="billing_city"
                              value={companyBranding.billing_city}
                              onChange={(e) => handleBrandingChange('billing_city', e.target.value)}
                              placeholder="City"
                              data-testid="billing-city-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="billing_state">State</Label>
                            <Input
                              id="billing_state"
                              value={companyBranding.billing_state}
                              onChange={(e) => handleBrandingChange('billing_state', e.target.value)}
                              placeholder="State"
                              data-testid="billing-state-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="billing_postal_code">ZIP</Label>
                            <Input
                              id="billing_postal_code"
                              value={companyBranding.billing_postal_code}
                              onChange={(e) => handleBrandingChange('billing_postal_code', e.target.value)}
                              placeholder="12345"
                              data-testid="billing-postal-code-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end pt-2">
                      <Button 
                        onClick={handleSaveBranding} 
                        disabled={!brandingChanged || savingBranding}
                        className="gap-1.5 text-xs sm:text-sm shrink-0"
                        data-testid="save-branding-button"
                      >
                        {savingBranding ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : brandingChanged ? (
                          <Check className="w-4 h-4" />
                        ) : null}
                        {savingBranding ? 'Saving...' : brandingChanged ? 'Save Changes' : 'No Changes'}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Current Logo Display */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-primary" />
                  <CardTitle>Current Logo</CardTitle>
                </div>
                <CardDescription>
                  The logo currently being displayed across the application.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="p-4 bg-muted rounded-lg">
                    <img 
                      key={brandingKey}
                      src={currentLogoInfo.logoUrl} 
                      alt="Current logo" 
                      className="h-20 w-auto object-contain"
                      data-testid="current-logo-preview"
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {currentLogoInfo.isForced ? (
                        <span className="text-amber-600">Manual Override Active</span>
                      ) : currentLogoInfo.seasonName === 'default' ? (
                        <span className="text-muted-foreground">Default Logo</span>
                      ) : (
                        <span className="text-primary">Seasonal: {currentLogoInfo.seasonName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentLogoInfo.isForced 
                        ? 'Seasonal switching is bypassed while override is active.'
                        : 'Logo changes automatically based on seasonal date windows.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Manual Override Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-primary" />
                  <CardTitle>Manual Override</CardTitle>
                </div>
                <CardDescription>
                  Force a specific logo to display, bypassing automatic seasonal switching.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Force Logo Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="force-logo-toggle" className="text-base font-medium">
                      Force Logo
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, the selected logo will always be displayed.
                    </p>
                  </div>
                  <Switch
                    id="force-logo-toggle"
                    checked={forceLogoEnabled}
                    onCheckedChange={handleForceLogoToggle}
                    disabled={!selectedForcedLogo && !forceLogoEnabled}
                    data-testid="force-logo-toggle"
                  />
                </div>

                {/* Logo Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-bold uppercase tracking-wide">
                    Select Logo
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {availableLogos.map((logo) => (
                      <button
                        key={logo.url}
                        type="button"
                        onClick={() => handleForcedLogoChange(logo.url)}
                        className={`
                          p-3 rounded-lg border-2 transition-all
                          ${selectedForcedLogo === logo.url 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50 hover:bg-muted/50'}
                        `}
                        data-testid={`logo-option-${logo.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <img 
                          src={logo.url} 
                          alt={logo.name} 
                          className="h-12 w-full object-contain mb-2"
                        />
                        <p className="text-xs font-medium text-center truncate">
                          {logo.name}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info box */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <p className="text-blue-800">
                    <strong>Seasonal Logo Windows:</strong>
                  </p>
                  <ul className="mt-2 text-blue-700 space-y-1 text-xs">
                    <li>• Valentines Day: Feb 1 - Feb 14</li>
                    <li>• St Patricks Day: Mar 1 - Mar 17</li>
                    <li>• Easter: Mar 15 - Apr 15</li>
                    <li>• Memorial Day: May 15 - May 31</li>
                    <li>• 4th of July: Jun 24 - Jul 7</li>
                    <li>• Halloween: Oct 1 - Oct 31</li>
                    <li>• Christmas: Nov 20 - Dec 26</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Invoice Branding Card */}
            <Card data-testid="invoice-branding-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  <CardTitle>Invoice Appearance</CardTitle>
                </div>
                <CardDescription>
                  Customize colors on your generated invoice PDFs. Changes apply to all new invoices.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Header Bar Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={companyBranding.invoice_header_bg_color || '#1e293b'}
                        onChange={(e) => handleBrandingChange('invoice_header_bg_color', e.target.value)}
                        className="w-10 h-9 rounded border cursor-pointer"
                        data-testid="invoice-header-bg-color"
                      />
                      <Input
                        value={companyBranding.invoice_header_bg_color || '#1e293b'}
                        onChange={(e) => handleBrandingChange('invoice_header_bg_color', e.target.value)}
                        placeholder="#1e293b"
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Header Text Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={companyBranding.invoice_header_text_color || '#ffffff'}
                        onChange={(e) => handleBrandingChange('invoice_header_text_color', e.target.value)}
                        className="w-10 h-9 rounded border cursor-pointer"
                        data-testid="invoice-header-text-color"
                      />
                      <Input
                        value={companyBranding.invoice_header_text_color || '#ffffff'}
                        onChange={(e) => handleBrandingChange('invoice_header_text_color', e.target.value)}
                        placeholder="#ffffff"
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Live Invoice Preview */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Preview</Label>
                  <div className="border rounded-lg p-4 bg-white dark:bg-zinc-950 space-y-3" data-testid="invoice-preview">
                    {/* Header: Logo/Company + Invoice number */}
                    <div className="flex justify-between items-start">
                      <div>
                        {companyBranding.company_logo_url ? (
                          <img src={companyBranding.company_logo_url} alt="" className="h-8 w-auto object-contain" />
                        ) : (
                          <p className="font-bold text-sm text-zinc-900 dark:text-zinc-100">{companyBranding.company_name || 'Company Name'}</p>
                        )}
                        <p className="text-[9px] text-zinc-400 mt-0.5">
                          {[companyBranding.billing_address_line1, [companyBranding.billing_city, companyBranding.billing_state, companyBranding.billing_postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '123 Main St, City, ST 12345'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] text-zinc-400 uppercase tracking-wide">Invoice</p>
                        <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100">INV-0001</p>
                      </div>
                    </div>
                    {/* Branded header row */}
                    <div
                      className="rounded-sm px-2 py-1.5 flex justify-between text-[9px] font-bold"
                      style={{
                        backgroundColor: companyBranding.invoice_header_bg_color || '#1e293b',
                        color: companyBranding.invoice_header_text_color || '#ffffff',
                      }}
                      data-testid="invoice-preview-header"
                    >
                      <span>Description</span>
                      <div className="flex gap-6">
                        <span>Qty</span>
                        <span>Price</span>
                        <span>Total</span>
                      </div>
                    </div>
                    {/* Sample items */}
                    <div className="space-y-1 text-[9px] text-zinc-600 dark:text-zinc-400">
                      <div className="flex justify-between px-2 py-0.5 border-b border-zinc-100 dark:border-zinc-800">
                        <span className="font-medium text-zinc-800 dark:text-zinc-200">AC System Repair</span>
                        <div className="flex gap-6">
                          <span>1</span>
                          <span>$750.00</span>
                          <span>$750.00</span>
                        </div>
                      </div>
                      <div className="flex justify-between px-2 py-0.5">
                        <span className="font-medium text-zinc-800 dark:text-zinc-200">Parts & Materials</span>
                        <div className="flex gap-6">
                          <span>1</span>
                          <span>$125.00</span>
                          <span>$125.00</span>
                        </div>
                      </div>
                    </div>
                    {/* Total */}
                    <div className="flex justify-end border-t border-zinc-200 dark:border-zinc-700 pt-1.5 pr-2">
                      <span className="text-[10px] font-bold text-zinc-900 dark:text-zinc-100">Total: $875.00</span>
                    </div>
                  </div>
                </div>

                {/* Contrast warning */}
                {(() => {
                  const bg = companyBranding.invoice_header_bg_color || '#1e293b';
                  const txt = companyBranding.invoice_header_text_color || '#ffffff';
                  const hexToLum = (hex) => {
                    const r = parseInt(hex.slice(1,3), 16) / 255;
                    const g = parseInt(hex.slice(3,5), 16) / 255;
                    const b = parseInt(hex.slice(5,7), 16) / 255;
                    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
                    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
                  };
                  try {
                    const l1 = hexToLum(bg), l2 = hexToLum(txt);
                    const ratio = (Math.max(l1,l2) + 0.05) / (Math.min(l1,l2) + 0.05);
                    if (ratio < 3) return (
                      <div className="flex items-center gap-2 p-2 rounded border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                        <p className="text-xs text-amber-700 dark:text-amber-400">Low contrast ({ratio.toFixed(1)}:1). Header text may be hard to read. Consider choosing more contrasting colors.</p>
                      </div>
                    );
                  } catch { /* invalid hex */ }
                  return null;
                })()}
              </CardContent>
            </Card>

            {/* Theme Customization Card */}
            <ThemeCustomizationCard />
          </TabsContent>

          {/* ====== COMMUNICATIONS TAB ====== */}
          {canAccessAllSettings && (
          <TabsContent value="communications" className="space-y-6">
            {/* --- Estimate Appointment Reminders Section --- */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-primary" />
                  <CardTitle>Estimate Appointment Reminders</CardTitle>
                </div>
                <CardDescription>
                  Automatically send day-before reminders to customers with scheduled estimate visits via SMS and email.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Enable Appointment Reminders</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Customers receive a reminder the day before their scheduled estimate appointment.</p>
                  </div>
                  <Switch
                    checked={estimateReminderEnabled}
                    onCheckedChange={toggleEstimateReminder}
                    disabled={savingEstReminder || !isOwner}
                    data-testid="estimate-reminder-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* --- Warehouse Notification Emails Section --- */}
            <h3 className="text-lg font-semibold" data-testid="section-notification-emails">Warehouse Notification Emails</h3>
            {!isOwner ? (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground">
                    Only the account owner can manage notification settings.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    <CardTitle>PO & Part Request Notifications</CardTitle>
                  </div>
                  <CardDescription>
                    Configure email notifications sent for PO and Part Request events.
                    These are tenant-wide defaults. Individual warehouses can override these settings.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {loadingNotificationSettings ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="po_received_email_to">
                            Notification Email — To
                          </Label>
                          <Input
                            id="po_received_email_to"
                            value={poNotificationSettings.po_received_email_to}
                            onChange={(e) => handleNotificationSettingChange('po_received_email_to', e.target.value)}
                            placeholder="office@company.com, manager@company.com"
                            data-testid="po-notification-to-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Comma-separated email addresses. These recipients receive the notification.
                          </p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="po_received_email_cc">
                            Notification Email — CC
                          </Label>
                          <Input
                            id="po_received_email_cc"
                            value={poNotificationSettings.po_received_email_cc}
                            onChange={(e) => handleNotificationSettingChange('po_received_email_cc', e.target.value)}
                            placeholder="accounting@company.com"
                            data-testid="po-notification-cc-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Optional. Additional recipients to CC on the notification.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>When to send the PO received email</Label>
                          <Select
                            value={poNotificationSettings.po_received_notify_mode}
                            onValueChange={(v) => handleNotificationSettingChange('po_received_notify_mode', v)}
                          >
                            <SelectTrigger data-testid="po-notification-mode-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fully_received" data-testid="po-notification-mode-fully-received">Only when the PO is fully received</SelectItem>
                              <SelectItem value="every_receipt" data-testid="po-notification-mode-every-receipt">Every receipt (including partial and stock-only)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            "Every receipt" sends one email each time received items are put into a warehouse or vehicle.
                          </p>
                        </div>
                      </div>
                      
                      {/* Validation Error */}
                      {notificationValidationError && (
                        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive">
                          {notificationValidationError}
                        </div>
                      )}
                      
                      {/* Info box */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                        <p className="text-blue-800 font-medium mb-2">How this works:</p>
                        <ul className="text-blue-700 space-y-1 text-xs">
                          <li>• Email is sent when a PO is fully received, or on every receipt if selected above</li>
                          <li>• If "Require Job Number" is ON, POs without a job number won't trigger emails</li>
                          <li>• If blank, no email notifications will be sent (feature is off)</li>
                          <li>• Individual warehouses can override these defaults in their settings</li>
                        </ul>
                      </div>
                      
                      {/* Job Number Requirement Toggle */}
                      <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                        <div className="space-y-1">
                          <Label htmlFor="require_job_number" className="font-medium">
                            Require Job Number for Email
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            When enabled, emails are only sent for POs that have a Job Number assigned.
                            Turn this off to also notify for stock POs (job number shown as "N/A").
                          </p>
                        </div>
                        <Switch
                          id="require_job_number"
                          checked={poNotificationSettings.require_job_number_for_po_received_email}
                          onCheckedChange={(checked) => handleNotificationSettingChange('require_job_number_for_po_received_email', checked)}
                          data-testid="require-job-number-toggle"
                        />
                      </div>
                      
                      <div className="flex justify-end pt-2">
                        <Button 
                          onClick={handleSaveNotificationSettings} 
                          disabled={!notificationSettingsChanged || savingNotificationSettings}
                          className="gap-1.5 text-xs sm:text-sm shrink-0"
                          data-testid="save-notification-settings-button"
                        >
                          {savingNotificationSettings ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : notificationSettingsChanged ? (
                            <Check className="w-4 h-4" />
                          ) : null}
                          {savingNotificationSettings ? 'Saving...' : notificationSettingsChanged ? 'Save Changes' : 'No Changes'}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* --- Event Notifications Section --- */}
            {isOwner && (
              <Card data-testid="event-notifications-card">
                <CardHeader>
                  <CardTitle className="text-lg">Event Notifications</CardTitle>
                  <CardDescription>
                    Configure which events send email notifications and who receives them.
                    Warehouse-specific overrides (above) take priority for warehouse-scoped events.
                  </CardDescription>
                  <p className="text-sm text-muted-foreground" data-testid="recipient-precedence-help">
                    Recipients are chosen in this order: Global &rarr; Warehouse override &rarr; Warehouse defaults &rarr; Event recipients.
                  </p>
                </CardHeader>
                <CardContent>
                  {loadingNotifEngine ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : notifEngineSettings ? (
                    <div className="space-y-6">
                      {/* Global Recipients */}
                      <div className="border rounded-lg p-4 space-y-4" data-testid="global-recipients-section">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={notifEngineSettings.global_enabled}
                            onCheckedChange={(checked) => handleNotifEngineGlobalChange('global_enabled', checked)}
                            data-testid="global-recipients-toggle"
                          />
                          <div>
                            <Label className="font-medium">Global Recipients</Label>
                            <p className="text-xs text-muted-foreground">
                              Global recipients receive all notification emails (in addition to event-specific recipients).
                            </p>
                          </div>
                        </div>
                        {notifEngineSettings.global_enabled && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-10">
                            <div className="space-y-1.5">
                              <Label className="text-sm">Global To</Label>
                              <Input
                                placeholder="ops@company.com, manager@company.com"
                                value={notifEngineSettings.global_to}
                                onChange={(e) => handleNotifEngineGlobalChange('global_to', e.target.value)}
                                data-testid="global-to-input"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-sm">Global CC</Label>
                              <Input
                                placeholder="finance@company.com"
                                value={notifEngineSettings.global_cc}
                                onChange={(e) => handleNotifEngineGlobalChange('global_cc', e.target.value)}
                                data-testid="global-cc-input"
                              />
                            </div>
                          </div>
                        )}
                        {notifEngineSettings.global_enabled && (
                          <div className="pl-10">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleSendTestEmail}
                              disabled={sendingTestEmail}
                              className="gap-1.5 text-xs sm:text-sm shrink-0"
                              data-testid="send-test-email-button"
                            >
                              {sendingTestEmail ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Send className="w-3.5 h-3.5" />
                              )}
                              {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Event Rules Table */}
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left p-3 font-medium text-sm">Event</th>
                              <th className="text-center p-3 font-medium text-sm w-20">Enabled</th>
                              <th className="text-left p-3 font-medium text-sm">To</th>
                              <th className="text-left p-3 font-medium text-sm">CC</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {notifEngineSettings.rules.map((rule) => (
                              <tr key={rule.event_key} data-testid={`event-row-${rule.event_key}`}>
                                <td className="p-3">
                                  <div className="text-sm font-medium">{rule.label}</div>
                                  <div className="text-xs text-muted-foreground">{rule.description}</div>
                                </td>
                                <td className="p-3 text-center">
                                  <Switch
                                    checked={rule.enabled}
                                    onCheckedChange={(checked) => handleNotifEngineRuleChange(rule.event_key, 'enabled', checked)}
                                    data-testid={`event-toggle-${rule.event_key}`}
                                  />
                                </td>
                                <td className="p-3">
                                  <Input
                                    placeholder="to@company.com"
                                    value={rule.to_emails}
                                    onChange={(e) => handleNotifEngineRuleChange(rule.event_key, 'to_emails', e.target.value)}
                                    disabled={!rule.enabled}
                                    className="text-sm h-8"
                                    data-testid={`event-to-${rule.event_key}`}
                                  />
                                </td>
                                <td className="p-3">
                                  <Input
                                    placeholder="cc@company.com"
                                    value={rule.cc_emails}
                                    onChange={(e) => handleNotifEngineRuleChange(rule.event_key, 'cc_emails', e.target.value)}
                                    disabled={!rule.enabled}
                                    className="text-sm h-8"
                                    data-testid={`event-cc-${rule.event_key}`}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Save button */}
                      <div className="flex justify-end">
                        <Button
                          onClick={handleSaveNotifEngine}
                          disabled={!notifEngineChanged || savingNotifEngine}
                          className="gap-1.5 text-xs sm:text-sm shrink-0"
                          data-testid="save-notif-engine-button"
                        >
                          {savingNotifEngine ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : notifEngineChanged ? (
                            <Check className="w-4 h-4" />
                          ) : null}
                          {savingNotifEngine ? 'Saving...' : notifEngineChanged ? 'Save Changes' : 'No Changes'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="py-4">
                      <p className="text-muted-foreground text-sm">
                        {notifEngineError || 'Failed to load notification settings.'}
                      </p>
                      <Button variant="outline" size="sm" onClick={loadNotifEngine} className="gap-1.5 mt-2" data-testid="retry-notif-engine-btn">
                        <RotateCcw className="w-3.5 h-3.5" />
                        Retry
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* --- Daily Activity Digest Section --- */}
            {isOwner && (
              <Card data-testid="daily-digest-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Daily Activity Digest
                  </CardTitle>
                  <CardDescription>
                    Receive a daily email summary of warehouse activity from the previous day.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {digestLoading ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between" data-testid="digest-enabled-toggle">
                        <div className="space-y-0.5">
                          <Label htmlFor="digest-enabled">Enable Daily Digest</Label>
                          <p className="text-xs text-muted-foreground">
                            Sends once per day at the configured time
                          </p>
                        </div>
                        <Switch
                          id="digest-enabled"
                          checked={digestSettings.daily_digest_enabled}
                          onCheckedChange={(v) => handleDigestChange('daily_digest_enabled', v)}
                          data-testid="digest-enabled-switch"
                        />
                      </div>
                      
                      {digestSettings.daily_digest_enabled && (
                        <div className="space-y-4 pt-2 border-t">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="digest-time">Send Time (Local)</Label>
                              <Input
                                id="digest-time"
                                type="time"
                                value={digestSettings.daily_digest_send_time_local}
                                onChange={(e) => handleDigestChange('daily_digest_send_time_local', e.target.value)}
                                data-testid="digest-send-time-input"
                              />
                              <p className="text-xs text-muted-foreground">
                                Timezone: {digestSettings.tenant_timezone}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between" data-testid="digest-wh-emails-toggle">
                            <div className="space-y-0.5">
                              <Label htmlFor="digest-wh-emails">Include Warehouse Emails</Label>
                              <p className="text-xs text-muted-foreground">
                                Also send to each warehouse's notification email (if set)
                              </p>
                            </div>
                            <Switch
                              id="digest-wh-emails"
                              checked={digestSettings.daily_digest_include_warehouse_emails}
                              onCheckedChange={(v) => handleDigestChange('daily_digest_include_warehouse_emails', v)}
                              data-testid="digest-wh-emails-switch"
                            />
                          </div>
                          
                          <div className="flex items-center gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handlePreviewDigest}
                              disabled={digestPreviewLoading}
                              data-testid="digest-preview-btn"
                            >
                              {digestPreviewLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                              Preview Yesterday's Digest
                            </Button>
                          </div>
                          
                          {digestPreview && (
                            <div className="border rounded-lg overflow-hidden mt-2" data-testid="digest-preview-panel">
                              <div className="bg-muted/50 px-3 py-2 text-xs text-muted-foreground flex justify-between items-center">
                                <span>{digestPreview.subject}</span>
                                <span>{digestPreview.recipient_count} recipient(s)</span>
                              </div>
                              <div className="p-3 max-h-96 overflow-y-auto text-sm" dangerouslySetInnerHTML={{ __html: digestPreview.html }} />
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex justify-end pt-2">
                        <Button
                          onClick={handleSaveDigest}
                          disabled={!digestChanged || digestSaving}
                          data-testid="digest-save-btn"
                        >
                          {digestSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                          {digestSaving ? 'Saving...' : digestChanged ? 'Save Digest Settings' : 'No Changes'}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* --- Email Settings Section --- */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4" data-testid="section-email-settings">Email Settings</h3>
            </div>
            <EmailSettingsPanel />

            {/* --- Phone & SMS Section --- */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4" data-testid="section-phone-sms">Phone & SMS</h3>
            </div>
            <CommunicationsSettings />
          </TabsContent>
          )}

          {/* ====== OPERATIONS TAB ====== */}
          {canAccessAllSettings && (
          <TabsContent value="operations" className="space-y-6">
            {/* --- Operations Settings Section --- */}
            <h3 className="text-lg font-semibold" data-testid="section-operations-settings">Operations Settings</h3>
            <Card data-testid="operations-settings-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-primary" />
                  <CardTitle>Truck Prep & Alerts</CardTitle>
                </div>
                <CardDescription>Configure operational automation settings for your company.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingOps ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="truck-prep-time">Truck Prep Alert Time</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Clock className="w-3.5 h-3.5 text-muted-foreground cursor-help" data-testid="truck-prep-time-tooltip" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              This alert runs at the selected time in your company's configured timezone.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Each morning at this time, the system scans today's schedule and alerts office staff about vehicles that need preparation.
                      </p>
                      <div className="flex items-center gap-3">
                        <Input
                          id="truck-prep-time"
                          type="time"
                          value={opsAlertTime}
                          onChange={(e) => setOpsAlertTime(e.target.value)}
                          className="w-36"
                          data-testid="truck-prep-alert-time-input"
                        />
                        {opsTimezone && (
                          <span className="text-sm text-muted-foreground" data-testid="truck-prep-timezone-display">
                            {opsTimezone.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      {opsTimezone && (
                        <p className="text-xs text-muted-foreground italic" data-testid="truck-prep-timezone-hint">
                          Alert runs in your company's timezone ({opsTimezone}). Change it in General settings.
                        </p>
                      )}
                    </div>

                    {/* Day-Before Alert Settings */}
                    <div className="pt-4 border-t space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="day-before-toggle">Day-Before Truck Prep Alert</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Clock className="w-3.5 h-3.5 text-muted-foreground cursor-help" data-testid="day-before-tooltip" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                When enabled, the system scans tomorrow's schedule the evening before and alerts office staff about vehicles that need preparation.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            id="day-before-toggle"
                            type="checkbox"
                            checked={dayBeforeEnabled}
                            onChange={(e) => setDayBeforeEnabled(e.target.checked)}
                            className="sr-only peer"
                            data-testid="day-before-alert-toggle"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Get an evening alert for tomorrow's jobs, so you can prepare vehicles overnight.
                      </p>
                      {dayBeforeEnabled && (
                        <div className="space-y-2 pl-1">
                          <Label htmlFor="day-before-time" className="text-sm">Day-Before Alert Time</Label>
                          <div className="flex items-center gap-3">
                            <Input
                              id="day-before-time"
                              type="time"
                              value={dayBeforeTime}
                              onChange={(e) => setDayBeforeTime(e.target.value)}
                              className="w-36"
                              data-testid="day-before-alert-time-input"
                            />
                            {opsTimezone && (
                              <span className="text-sm text-muted-foreground" data-testid="day-before-timezone-display">
                                {opsTimezone.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Readiness Goal Percentage */}
                    <div className="pt-4 border-t space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="readiness-goal">Readiness Goal</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" data-testid="readiness-goal-tooltip" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              Target daily readiness rate for your company. Shown as a goal line on the Weekly Truck Prep Summary chart.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Set the target readiness percentage (1-100). This appears as a goal line on the weekly report chart.
                      </p>
                      <div className="flex items-center gap-2">
                        <Input
                          id="readiness-goal"
                          type="number"
                          min={1}
                          max={100}
                          value={readinessGoalPct}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v)) setReadinessGoalPct(Math.min(100, Math.max(1, v)));
                          }}
                          className="w-24"
                          data-testid="readiness-goal-input"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </div>
                    <Button
                      onClick={handleSaveOps}
                      disabled={savingOps}
                      data-testid="save-operations-btn"
                    >
                      {savingOps ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                      Save Operations Settings
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* --- PO Departments Section --- */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4" data-testid="section-departments">PO Departments</h3>
            </div>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-primary" />
                    <CardTitle>PO Departments</CardTitle>
                  </div>
                  <Button size="sm" onClick={openAddDepartment}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Department
                  </Button>
                </div>
                <CardDescription>
                  Configure departments for categorizing Purchase Orders. Examples: Pool Service, Electrical, HVAC.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingDepartments ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : departments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No departments configured yet.</p>
                    <p className="text-sm mt-1">Add departments to categorize your POs.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {departments.map(dept => (
                      <div
                        key={dept.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          dept.is_active ? 'bg-background' : 'bg-muted/50 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Tag className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <span className={dept.is_active ? '' : 'line-through'}>{dept.name}</span>
                            {!dept.is_active && (
                              <span className="text-xs text-muted-foreground ml-2">(Inactive)</span>
                            )}
                            {dept.spend_limit_cents !== null && dept.spend_limit_cents !== undefined && (
                              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                Limit: {dept.spend_limit_display || `$${(dept.spend_limit_cents / 100).toFixed(2)}`}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditDepartment(dept)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Switch
                            checked={dept.is_active}
                            onCheckedChange={() => toggleDepartmentActive(dept)}
                          />
                          {isOwner && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => {
                                setDeletingDepartment(dept);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Info card about departments */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <Tag className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How departments work</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                      <li>Departments are optional tags for Purchase Orders</li>
                      <li>They help organize and filter POs by work category</li>
                      <li><strong>Spend limits:</strong> If set, department limit takes priority over technician limit</li>
                      <li>POs over the department limit are auto-rejected with a clear reason</li>
                      <li>Deactivating a department hides it from new POs but preserves existing data</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* --- Tags Section --- */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4" data-testid="section-tags">Tags</h3>
            </div>
            <TagManagement />
          </TabsContent>
          )}

          {/* ====== SALES TAB ====== */}
          {canAccessAllSettings && (
          <TabsContent value="sales" className="space-y-6">
            {/* --- Estimates Section --- */}
            <h3 className="text-lg font-semibold" data-testid="section-estimates">Estimates & Invoices</h3>
            {/* Default Tax Rate Card */}
            <Card data-testid="default-tax-rate-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Default Tax Rate
                </CardTitle>
                <CardDescription>
                  Automatically applied to new estimates and invoices. Existing records are not affected.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingEstRec ? (
                  <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : (
                  <>
                    <div className="space-y-2 max-w-xs">
                      <Label htmlFor="default-tax-rate">Default Tax Rate (%)</Label>
                      <Input
                        id="default-tax-rate"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={defaultTaxRate}
                        onChange={(e) => setDefaultTaxRate(e.target.value)}
                        placeholder="e.g. 8.25"
                        data-testid="default-tax-rate-input"
                      />
                      <p className="text-xs text-muted-foreground">Enter 0 for tax-exempt. Leave blank to disable auto-tax.</p>
                    </div>
                    <div className="flex justify-end">
                      <Button onClick={saveTaxRate} disabled={savingTaxRate || !taxRateChanged} data-testid="save-tax-rate-btn">
                        {savingTaxRate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                        {savingTaxRate ? 'Saving...' : taxRateChanged ? 'Save Tax Rate' : 'No Changes'}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Default Recommendation Text
                </CardTitle>
                <CardDescription>
                  Set default recommendation text for Good / Better / Best estimate options.
                  These defaults auto-fill when creating new options — each estimate can still override them.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {loadingEstRec ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="rec-good">Good Option</Label>
                      <Textarea
                        id="rec-good"
                        value={estRecDefaults.estimate_rec_text_good}
                        onChange={(e) => setEstRecDefaults(prev => ({ ...prev, estimate_rec_text_good: e.target.value }))}
                        placeholder="e.g., Reliable and budget-friendly — covers the essentials."
                        rows={2}
                        className="resize-none"
                        data-testid="rec-default-good"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rec-better">Better Option</Label>
                      <Textarea
                        id="rec-better"
                        value={estRecDefaults.estimate_rec_text_better}
                        onChange={(e) => setEstRecDefaults(prev => ({ ...prev, estimate_rec_text_better: e.target.value }))}
                        placeholder="e.g., Best balance of cost and long-term reliability."
                        rows={2}
                        className="resize-none"
                        data-testid="rec-default-better"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rec-best">Best Option</Label>
                      <Textarea
                        id="rec-best"
                        value={estRecDefaults.estimate_rec_text_best}
                        onChange={(e) => setEstRecDefaults(prev => ({ ...prev, estimate_rec_text_best: e.target.value }))}
                        placeholder="e.g., Premium solution with maximum durability and warranty coverage."
                        rows={2}
                        className="resize-none"
                        data-testid="rec-default-best"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button onClick={saveEstRecDefaults} disabled={savingEstRec || !estRecChanged} data-testid="save-rec-defaults">
                        {savingEstRec ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                        Save Defaults
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* --- Payments Section --- */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4" data-testid="section-payments">Payments</h3>
            </div>
            {!isOwner ? (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground">
                    Only the account owner can manage payment settings.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Default Payment Methods */}
                <Card data-testid="default-payment-methods-card">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <div>
                        <CardTitle className="text-base">Default Payment Methods</CardTitle>
                        <CardDescription className="mt-0.5">
                          These defaults apply to newly created invoices. You can still override payment methods on an individual invoice.
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {[
                        { key: 'card', label: 'Card (in-person)', desc: 'Technician charges customer card on-site', icon: CreditCard },
                        { key: 'cash', label: 'Cash', desc: 'Cash collection by technician', icon: FileText },
                        { key: 'check', label: 'Check', desc: 'Check collection by technician', icon: FileText },
                        { key: 'online', label: 'Online / Payment Link', desc: 'Customer pays via emailed Stripe link', icon: Globe },
                      ].map(m => {
                        const enabled = defaultPaymentMethods[m.key] !== false;
                        return (
                          <label
                            key={m.key}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              enabled ? 'border-primary/20 bg-primary/[0.03]' : 'border-muted-foreground/15 bg-muted/30 opacity-60'
                            } ${savingPaymentMethods ? 'pointer-events-none opacity-70' : 'hover:bg-accent/40'}`}
                            data-testid={`default-method-${m.key}`}
                          >
                            <input
                              type="checkbox"
                              className="rounded border-muted-foreground/30 h-4 w-4 accent-primary shrink-0"
                              checked={enabled}
                              onChange={() => toggleDefaultPaymentMethod(m.key)}
                              disabled={savingPaymentMethods}
                            />
                            <m.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium leading-tight">{m.label}</p>
                              <p className="text-[11px] text-muted-foreground leading-tight">{m.desc}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {/* ACH sub-toggle — nested under Online */}
                    {(() => {
                      const onlineEnabled = defaultPaymentMethods.online !== false;
                      const achEnabled = !!defaultPaymentMethods.ach;
                      return (
                        <div className="mt-2.5 sm:ml-[50%] sm:pl-1.5">
                          <label
                            className={`flex items-center gap-3 p-3 ml-4 rounded-lg border cursor-pointer transition-colors ${
                              !onlineEnabled ? 'border-muted-foreground/10 bg-muted/20 opacity-40 cursor-not-allowed' :
                              achEnabled ? 'border-primary/20 bg-primary/[0.03]' : 'border-muted-foreground/15 bg-muted/30 opacity-60'
                            } ${savingPaymentMethods ? 'pointer-events-none opacity-70' : onlineEnabled ? 'hover:bg-accent/40' : ''}`}
                            data-testid="default-method-ach"
                          >
                            <input
                              type="checkbox"
                              className="rounded border-muted-foreground/30 h-4 w-4 accent-primary shrink-0"
                              checked={achEnabled}
                              onChange={() => toggleDefaultPaymentMethod('ach')}
                              disabled={savingPaymentMethods || !onlineEnabled}
                            />
                            <CreditCard className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium leading-tight">ACH Bank Transfer</p>
                              <p className="text-[11px] text-muted-foreground leading-tight">
                                {onlineEnabled ? 'Customer pays via bank transfer (Stripe)' : 'Enable Online to use ACH'}
                              </p>
                            </div>
                          </label>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
                <StripeConnectSettings />
              </div>
            )}
          </TabsContent>
          )}

          {/* ====== INTEGRATIONS TAB ====== */}
          {canAccessAllSettings && (
          <TabsContent value="integrations" className="space-y-6">
            {/* --- Integrations Section --- */}
            <h3 className="text-lg font-semibold" data-testid="section-integrations">Integrations</h3>
            {!isOwner ? (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground">
                    Only the account owner can manage integrations.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* ─── Integration Tiles (3-across on desktop) ─── */}
                {!stDetailOpen && !hcpDetailOpen && !gbpDetailOpen && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="integration-tiles">
                    {/* ServiceTitan Tile */}
                    <Card className="cursor-pointer hover:border-primary/40 transition-colors group" onClick={() => setStDetailOpen(true)} data-testid="st-integration-tile">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <img src="/crm-logos/servicetitan.png" alt="ServiceTitan" className="w-9 h-9 rounded-lg object-contain bg-white border p-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm font-semibold truncate">ServiceTitan</h3>
                              <Badge className="text-[10px] bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 shrink-0">CRM</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">Sync customers, jobs, materials, and warehouses.</p>
                            <Button variant="outline" size="sm" className="h-7 text-xs group-hover:border-primary/60">
                              Manage
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Housecall Pro Tile */}
                    <Card className="cursor-pointer hover:border-primary/40 transition-colors group" onClick={() => setHcpDetailOpen(true)} data-testid="hcp-integration-tile">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <img src="/crm-logos/hcp.png" alt="Housecall Pro" className="w-9 h-9 rounded-lg object-contain bg-white border p-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm font-semibold truncate">Housecall Pro</h3>
                              {hcpStatus?.configured && hcpStatus.connection_status === 'connected' ? (
                                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 shrink-0">Connected</Badge>
                              ) : (
                                <Badge className="text-[10px] bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 shrink-0">Not Connected</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">Sync jobs and customers. Map locations to warehouses.</p>
                            <Button variant="outline" size="sm" className="h-7 text-xs group-hover:border-primary/60">
                              {hcpStatus?.configured && hcpStatus?.connection_status === 'connected' ? 'Manage' : 'Connect'}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Google Business Profile Tile */}
                    <Card className="cursor-pointer hover:border-primary/40 transition-colors group" onClick={() => setGbpDetailOpen(true)} data-testid="gbp-integration-tile">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <img src="/crm-logos/google.png" alt="Google Business" className="w-9 h-9 rounded-lg object-contain bg-white border p-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm font-semibold truncate">Google Business</h3>
                              <Badge className="text-[10px] bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 shrink-0">Coming Soon</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">Sync reviews and manage your reputation across locations.</p>
                            <Button variant="outline" size="sm" className="h-7 text-xs group-hover:border-primary/60">
                              Learn More
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* ─── Expanded Detail (accordion: only one at a time) ─── */}
                {stDetailOpen && (
                  <Card data-testid="st-detail-panel">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Button variant="ghost" size="sm" onClick={() => setStDetailOpen(false)} data-testid="st-back-btn">
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <img src="/crm-logos/servicetitan.png" alt="ServiceTitan" className="w-8 h-8 rounded-lg object-contain bg-white border p-0.5" />
                        <div>
                          <h2 className="text-base font-bold">ServiceTitan Integration</h2>
                          <p className="text-xs text-muted-foreground">Sync your ServiceTitan data with Stock & Load inventory.</p>
                        </div>
                      </div>
                      <ServiceTitanSettings />
                    </CardContent>
                  </Card>
                )}

                {hcpDetailOpen && (
                  <Card data-testid="hcp-detail-panel">
                    <CardContent className="pt-6">
                      <HousecallProSettings onBack={() => setHcpDetailOpen(false)} />
                    </CardContent>
                  </Card>
                )}

                {gbpDetailOpen && (
                  <Card data-testid="gbp-detail-panel">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Button variant="ghost" size="sm" onClick={() => setGbpDetailOpen(false)} data-testid="gbp-back-btn">
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <img src="/crm-logos/google.png" alt="Google Business" className="w-8 h-8 rounded-lg object-contain bg-white border p-0.5" />
                        <div>
                          <h2 className="text-base font-bold">Google Business Profile</h2>
                          <p className="text-xs text-muted-foreground">Sync reviews and manage your online reputation.</p>
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                        <p className="font-medium text-blue-800">Coming Soon</p>
                        <p className="text-blue-700 mt-1">
                          Connect your Google Business Profile to automatically sync customer reviews, reply directly from Stock & Load, and monitor your reputation across all locations.
                        </p>
                        <p className="text-blue-600 mt-2 text-xs">
                          In the meantime, you can manually add and manage reviews from the <a href="/marketing/reviews" className="underline font-medium">Reviews</a> page.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* --- Data Migration Section --- */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4" data-testid="section-migration">Data Migration</h3>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RotateCcw className="w-5 h-5" />
                  Data Migration
                </CardTitle>
                <CardDescription>
                  Import your data from another CRM system using a guided wizard.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border rounded-xl p-6 bg-muted/20">
                    <h3 className="font-semibold mb-2">Migration Wizard</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Migrate your customers and jobs from Housecall Pro, Jobber, Markate, ServiceTitan, or any CSV file.
                      The wizard guides you through uploading, mapping fields, reviewing matches, and importing in the correct order.
                    </p>
                    <div className="flex items-center gap-4">
                      <Button onClick={() => window.location.href = '/migration-wizard'} data-testid="btn-launch-wizard">
                        Launch Migration Wizard
                      </Button>
                      <Button variant="outline" onClick={() => window.location.href = '/import-history'} data-testid="btn-import-history">
                        View Import History
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { name: 'Housecall Pro', logo: '/crm-logos/hcp.png' },
                      { name: 'Jobber', logo: '/crm-logos/jobber.png' },
                      { name: 'Markate', logo: '/crm-logos/markate.png' },
                      { name: 'ServiceTitan', logo: '/crm-logos/servicetitan.png' },
                    ].map(c => (
                      <div key={c.name} className="border rounded-lg p-3 flex items-center justify-center h-16 bg-white">
                        <img src={c.logo} alt={c.name} className="max-h-8 max-w-[100px] object-contain" />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}

        </Tabs>
        
        {/* Department Add/Edit Modal */}
        {/* Add/Edit Department Drawer */}
        <AppDrawer
          open={departmentModalOpen}
          onOpenChange={(open) => {
            setDepartmentModalOpen(open);
            if (!open) {
              setEditingDepartment(null);
              setDepartmentName('');
              setDepartmentSpendLimit('');
              setDepartmentHasSpendLimit(false);
            }
          }}
          title={editingDepartment ? 'Edit Department' : 'Add Department'}
          description={editingDepartment ? 'Update department settings' : 'Create a new PO department'}
          size="md"
          isDirty={!editingDepartment && !!departmentName.trim()}
          data-testid="department-drawer"
          footer={
            <>
              <Button variant="outline" onClick={() => setDepartmentModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveDepartment} disabled={savingDepartment} data-testid="save-department-btn">
                {savingDepartment && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingDepartment ? 'Save Changes' : 'Create Department'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Department Name</Label>
              <Input
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                placeholder="e.g., Pool Service, Electrical, HVAC"
                autoFocus
                data-testid="department-name-input"
              />
            </div>
            
            {/* Phase 7A: Spend Limit Configuration */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Department Spend Limit</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Override technician limits for POs in this department
                  </p>
                </div>
                <Switch
                  checked={departmentHasSpendLimit}
                  onCheckedChange={(checked) => {
                    setDepartmentHasSpendLimit(checked);
                    if (!checked) setDepartmentSpendLimit('');
                  }}
                  data-testid="department-spend-limit-toggle"
                />
              </div>
              
              {departmentHasSpendLimit && (
                <div className="space-y-2">
                  <Label className="text-sm">Limit Amount ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={departmentSpendLimit}
                    onChange={(e) => setDepartmentSpendLimit(e.target.value)}
                    placeholder="e.g., 500.00"
                    data-testid="department-spend-limit-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {departmentSpendLimit === '0' 
                      ? 'Setting to $0 will block all PO spending under this department'
                      : 'POs exceeding this amount will be auto-rejected'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </AppDrawer>
        
        {/* Delete Department Confirmation */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Department?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete "{deletingDepartment?.name}". Existing POs using this department will have it cleared. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteDepartment} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default CompanySettings;
